<template>
  <f7-page :page-content="true">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title v-if="id && !readonly" sliding>Editar item enriquecido</f7-nav-title>
      <f7-nav-title v-else-if="id" sliding>Ficha del item</f7-nav-title>
      <f7-nav-title v-else sliding>Nuevo item enriquecido</f7-nav-title>
      <f7-nav-right>
        <f7-button
          v-if="!readonly"
          fill
          icon-f7="floppy_disk"
          text="Guardar"
          class="display-flex"
          @click="$refs.rform.saveItem()"
        ></f7-button>
        <template slot="right" v-else>
          <div class="menu-item menu-item-dropdown bg-color-primary">
            <div class="menu-item-content"><i class="f7-icons">ellipsis_vertical</i></div>
            <div class="menu-dropdown menu-dropdown-right bg-color-primary">
              <div class="menu-dropdown-content bg-color-primary text-align-right">
                <div class="menu-dropdown-item menu-close pointer" @click="editar">{{$t('common.editar')}}</div>
                <div class="menu-dropdown-item menu-close pointer" @click="reclasificar">Reclasificar</div>
                <div class="menu-dropdown-divider"></div>
                <div class="menu-dropdown-item menu-close pointer text-color-red" @click="deleteItem">{{$t('common.eliminar')}}</div>
              </div>
            </div>
          </div>
        </template>
      </f7-nav-right>
    </f7-navbar>

    <r-form
      ref="rform"
      v-slot="{rform}"
      :itemId="id"
      :beforeSave="beforeSave"
      @save="onSave"
      @loadItem="onLoadItem"
      :readonly="readonly"
      :dbAdapter="ItemEnriquecido"
      :defaultData="defaultData"
    >
      <div class="ficha" v-cell-titles>

        <!-- ============ CABECERA ============ -->
        <div class="head">
          <div class="head-row">
            <div class="id-block">
              <div class="eyebrow">Identificador canónico</div>
              <div class="id-line">
                <h1 class="mono" :title="rform.formData.id || ''">{{ rform.formData.id || 'Nuevo item' }}</h1>
                <f7-link v-if="rform.formData.id" class="copy-btn" title="Copiar ID completo" @click="copiar(rform.formData.id)"><i class="f7-icons">doc_on_doc</i></f7-link>
              </div>
              <div class="sub">{{ subtituloItem(rform.formData) }}</div>
              <div class="badges">
                <span v-if="rform.formData.naturaleza" class="badge b-teal"><span class="dot"></span>Estrategia {{ rform.formData.naturaleza }}</span>
                <span v-if="rform.formData.tipo" class="badge b-slate">Tipo {{ rform.formData.tipo }} · {{ labelTipo(rform.formData.tipo) }}</span>
                <span v-for="(s, si) in (rform.formData.subtipos || [])" :key="'hb-' + si" class="badge b-slate">{{ s }}</span>
                <span v-if="rform.formData.estado" class="badge" :class="rform.formData.estado === 'ACTIVO' ? 'b-ok' : 'b-warn'"><span class="dot"></span>{{ rform.formData.estado }}</span>
                <span v-if="rform.formData.posibleIdentificadorErroneo" class="badge b-bad" :title="rform.formData.motivoPosibleIdentificadorErroneo || ''">⚠ Identificador dudoso</span>
              </div>
            </div>
            <div class="head-right">
              <div class="head-right-label">Clase de item</div>
              <div class="mono head-clase">{{ claseItem(rform.formData) }}</div>
              <div class="head-right-label margin-top-half">En servicio desde</div>
              <div class="head-since">{{ rform.formData.fechaCreacion ? formatFecha(rform.formData.fechaCreacion) : '—' }}</div>
            </div>
          </div>
        </div>

        <!-- ============ KPIs ============ -->
        <div class="kpis">
          <div class="kpi">
            <div class="k-label">Próxima inspección</div>
            <div class="k-val">{{ rform.formData.proximaInspeccion ? formatFecha(rform.formData.proximaInspeccion) : '—' }}</div>
            <div class="k-note">
              <span v-if="rform.formData.proximaInspeccionManual" class="prox-insp-badge prox-insp-manual" v-tooltip="'Fijada a mano: el sync no la recalcula'">manual</span>
              <span v-else class="prox-insp-badge prox-insp-auto" v-tooltip="'Calculada desde el evento de inspección más reciente'">automática</span>
            </div>
            <div v-if="meterProximaPct(rform.formData) != null" class="meter"><i :style="{ width: meterProximaPct(rform.formData) + '%' }"></i></div>
          </div>
          <div class="kpi">
            <div class="k-label">Última inspección</div>
            <div class="k-val">{{ rform.formData.fechaUltimaInspeccion ? formatFecha(rform.formData.fechaUltimaInspeccion) : '—' }}</div>
            <div class="k-note">{{ formatDireccion(rform.formData.direccionUltimaInspeccion) }}</div>
          </div>
          <div class="kpi">
            <div class="k-label">Último mantenimiento</div>
            <div class="k-val">{{ rform.formData.fechaUltimoMantenimiento ? formatFecha(rform.formData.fechaUltimoMantenimiento) : '—' }}</div>
            <div class="k-note">{{ rform.formData.fechaUltimoMantenimiento ? '' : 'sin eventos de mantenimiento' }}</div>
          </div>
          <div class="kpi">
            <div class="k-label">Materias</div>
            <div class="k-val">{{ (rform.formData.materias || []).length || '—' }}</div>
            <div class="k-note" :title="(rform.formData.materias || []).join(', ')">{{ (rform.formData.materias || []).join(', ') }}</div>
          </div>
        </div>

        <!-- ============ PESTAÑAS ============ -->
        <div class="tabs-mock">
          <button
            v-for="t in pestanas(rform.formData)"
            :key="'tab-' + t.key"
            class="tab"
            :class="{ active: tabActiva === t.key }"
            type="button"
            @click="tabActiva = t.key"
          >{{ t.label }} <span v-if="t.count" class="pill">{{ t.count }}</span><span v-else-if="t.ia" class="pill pill-ia">IA</span></button>
        </div>

        <!-- ---------- IDENTIDAD ---------- -->
        <div v-show="tabActiva === 'identidad'">
          <!-- Avisos del último sync -->
          <div v-if="(rform.formData.warningsSync || []).length" class="note">
            <div class="note-title">⚠ Avisos del último sync</div>
            <div v-for="(w, wi) in rform.formData.warningsSync" :key="'warn-' + wi" class="warning-line">{{ w }}</div>
          </div>

          <!-- Modo edición: los inputs de siempre -->
          <div v-if="!rform.readonly" class="panel-card">
            <h3>Identidad <span class="coll">· edición</span></h3>
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="50" medium="50" small="100">
                <r-input
                  outline
                  class="col-100"
                  :class="{'codigo-readonly': !!rform.formData.id}"
                  floating-label
                  label="ID canónico"
                  :form="rform"
                  type="text"
                  name="id"
                  :readonly="!!rform.formData.id"
                  :overrideReadonly="!!rform.formData.id"
                ></r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Tipo" :form="rform" type="select" name="tipo">
                  <option value=""></option>
                  <option v-for="t in tiposItem" :key="'tipo-' + t.valor" :value="t.valor">{{t.valor}} — {{t.label}}</option>
                </r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Naturaleza" :form="rform" type="select" name="naturaleza">
                  <option value=""></option>
                  <option value="FIJ">FIJ — Fijo</option>
                  <option value="MOV">MOV — Móvil</option>
                </r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Estado" :form="rform" type="select" name="estado">
                  <option value="ACTIVO">Activo</option>
                  <option value="PROVISIONAL">Provisional</option>
                </r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Expediente origen" :form="rform" type="text" name="expedienteOrigen"></r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Titular" :form="rform" type="text" name="titularActual.razonSocial"></r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="NIF titular" :form="rform" type="text" name="titularActual.nif"></r-input>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input
                  outline
                  class="col-100"
                  floating-label
                  label="Próxima inspección"
                  :form="rform"
                  type="datepicker"
                  :clearButton="true"
                  name="proximaInspeccion"
                  :calendar-params="{closeOnSelect:true,routableModals:false, dateFormat: { year:'numeric', month: '2-digit', day: '2-digit'} }"
                ></r-input>
                <div class="prox-insp-hint">
                  <span v-if="rform.formData && rform.formData.proximaInspeccionManual" class="prox-insp-badge prox-insp-manual" v-tooltip="'Fijada a mano: el sync no la recalcula'">manual</span>
                  <span v-else class="prox-insp-badge prox-insp-auto" v-tooltip="'Calculada desde el evento de inspección más reciente'">automática</span>
                  <f7-link v-if="rform.formData && rform.formData.proximaInspeccionManual && !rform.readonly"
                    @click="volverProximaAutomatica" v-tooltip="'Recalcular desde el evento de inspección más reciente al guardar'">
                    Volver a automática
                  </f7-link>
                </div>
              </f7-col>
              <f7-col width="50" medium="50" small="100">
                <r-input outline class="col-100" floating-label label="Posible identificador erróneo" :form="rform" type="check" name="posibleIdentificadorErroneo"></r-input>
              </f7-col>
              <f7-col width="100" v-if="rform.formData.posibleIdentificadorErroneo">
                <r-input outline class="col-100 observaciones-input" floating-label label="Motivo" :form="rform" type="textarea" name="motivoPosibleIdentificadorErroneo" resizable></r-input>
              </f7-col>
            </f7-row>
          </div>

          <!-- Modo lectura: dl estilo mockup -->
          <div v-else class="panel-card">
            <h3>Identidad canónica <span class="coll">· items_enriquecidos</span></h3>
            <p class="h-desc">{{ subtituloItem(rform.formData) }}</p>
            <div class="col-2">
              <dl class="dl">
                <dt>ID canónico</dt>
                <dd>
                  <div class="valor-copiable">
                    <span class="mono kv-hl kv-id" :title="rform.formData.id || ''">{{ rform.formData.id || '—' }}</span>
                    <f7-link v-if="rform.formData.id" class="copy-btn" title="Copiar ID completo" @click="copiar(rform.formData.id)"><i class="f7-icons">doc_on_doc</i></f7-link>
                  </div>
                </dd>
                <dt>Estrategia de identidad</dt><dd>{{ etiquetaEstrategia(rform.formData.naturaleza) }}</dd>
                <dt>Tipo</dt><dd>{{ rform.formData.tipo ? (rform.formData.tipo + ' — ' + labelTipo(rform.formData.tipo)) : '—' }}</dd>
                <dt>Materias</dt><dd>{{ (rform.formData.materias || []).join(', ') || '—' }}</dd>
                <dt>Titular</dt><dd>{{ (rform.formData.titularActual && rform.formData.titularActual.razonSocial) || '—' }}</dd>
                <dt>NIF titular</dt><dd class="mono">{{ (rform.formData.titularActual && rform.formData.titularActual.nif) || '—' }}</dd>
              </dl>
              <dl class="dl">
                <dt>Estado</dt><dd><span class="st" :class="rform.formData.estado === 'ACTIVO' ? 'st-cur' : 'st-soon'"><span class="dot"></span>{{ rform.formData.estado || '—' }}</span></dd>
                <dt>Expediente fuente</dt>
                <dd>
                  <div v-if="rform.formData.expedienteOrigen" class="valor-copiable">
                    <a :href="urlVerExpediente(rform.formData.expedienteOrigen)" target="_blank" rel="noopener" class="external evento-expediente-link" :title="rform.formData.expedienteOrigen"><code>{{ codigoExpedienteOrigen(rform.formData) }}</code> <i class="f7-icons evento-link-icon">arrow_up_right_square</i></a>
                    <f7-link class="copy-btn" title="Copiar código del expediente" @click="copiar(codigoExpedienteOrigen(rform.formData))"><i class="f7-icons">doc_on_doc</i></f7-link>
                  </div>
                  <span v-else>—</span>
                </dd>
                <dt>Expedientes vinculados</dt><dd>{{ (rform.formData.expedientesVinculados || []).length }}</dd>
                <dt>Ids alias</dt>
                <dd>
                  <template v-if="(rform.formData.idsAlias || []).length">
                    <span v-for="(a, ai) in rform.formData.idsAlias" :key="'alias-' + ai" class="tag mono">{{ a }}</span>
                  </template>
                  <span v-else>—</span>
                </dd>
                <dt>Próxima inspección</dt>
                <dd>
                  {{ rform.formData.proximaInspeccion ? formatFecha(rform.formData.proximaInspeccion) : '—' }}
                  <span v-if="rform.formData.proximaInspeccionManual" class="prox-insp-badge prox-insp-manual">manual</span>
                  <span v-else-if="rform.formData.proximaInspeccion" class="prox-insp-badge prox-insp-auto">automática</span>
                </dd>
                <dt>Identificador dudoso</dt>
                <dd>
                  <template v-if="rform.formData.posibleIdentificadorErroneo">
                    <span class="st st-bad">Sí</span>
                    <div v-if="rform.formData.motivoPosibleIdentificadorErroneo" class="text-color-gray" style="font-size:12px; margin-top:2px;">{{ rform.formData.motivoPosibleIdentificadorErroneo }}</div>
                  </template>
                  <span v-else>No</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <!-- ---------- CLASIFICACIÓN ---------- -->
        <div v-show="tabActiva === 'clasificacion'">
          <div class="panel-card">
            <h3>
              Clasificación del item <span class="coll">· items_clasificacion</span>
              <a
                v-if="idClasificacionNavegable(rform.formData)"
                :href="urlVerClasificacion(idClasificacionNavegable(rform.formData))"
                target="_blank"
                rel="noopener"
                class="external clasif-link"
                title="Abrir la clasificación en nueva pestaña para revisarla o modificarla"
              ><i class="f7-icons" style="font-size:14px;">arrow_up_right_square</i> Ir a la clasificación</a>
              <span v-if="rform.formData.tipificacionParcial" class="parcial-badge">⚠ Tipificación parcial — completar a mano</span>
              <span v-else-if="rform.formData.clasificacionAplicada" class="text-color-gray margin-left" style="font-size:11px;">
                resuelta {{ formatFecha(rform.formData.clasificacionAplicada.fechaResolucion) }}
              </span>
            </h3>
            <p class="h-desc">Eje del activo: tipología deducida en el sync y ruta de subtipos aplicada al catálogo de características.</p>
            <div class="clasif-chips">
              <span class="chip-tipologia chip-activo chip-raiz">{{ (rform.formData.clasificacionAplicada && rform.formData.clasificacionAplicada.tipo) || rform.formData.tipo }}</span>
              <span
                v-for="(s, si) in (rform.formData.subtipos || [])"
                :key="'clasif-sub-' + si"
                class="chip-tipologia chip-activo"
              >{{ s }}</span>
              <span v-if="rform.formData.tipificacionParcial" class="chip-tipologia chip-pendiente" title="Subtipos pendientes de completar">…?</span>
            </div>
            <!-- Completar tipología a mano cuando quedó parcial -->
            <div v-if="rform.formData.tipificacionParcial && !rform.readonly" class="completar-tipologia">
              <label class="caract-label">Completar tipología</label>
              <select class="completar-select" @change="onCompletarTipologia(rform, $event.target.value)">
                <option value="">— Elige la tipología completa —</option>
                <option
                  v-for="opt in opcionesCompletarTipologia(rform.formData)"
                  :key="'compl-' + opt.key"
                  :value="opt.key"
                >{{ opt.label }}</option>
              </select>
              <span class="text-color-gray" style="font-size:11px;">Al elegir, pulsa Guardar para fijarla. El próximo sync la respetará.</span>
            </div>
            <!-- Detalle de la clasificación (consulta a items_clasificacion) -->
            <div v-if="clasificacionItem" class="col-2 margin-top">
              <dl class="dl">
                <dt>Clase (código)</dt><dd><span class="mono kv-hl">{{ clasificacionItem.codigo || '—' }}</span></dd>
                <dt v-if="clasificacionItem.denominacion">Denominación</dt><dd v-if="clasificacionItem.denominacion">{{ clasificacionItem.denominacion }}</dd>
                <dt>Tipo</dt><dd>{{ rform.formData.tipo ? (rform.formData.tipo + ' — ' + labelTipo(rform.formData.tipo)) : '—' }}</dd>
                <dt>Naturaleza</dt><dd>{{ clasificacionItem.naturaleza || rform.formData.naturaleza || '—' }}</dd>
                <dt v-if="clasificacionItem.reglamento">Reglamento</dt><dd v-if="clasificacionItem.reglamento">{{ clasificacionItem.reglamento }}</dd>
                <dt v-if="clasificacionItem.itc">ITC</dt><dd v-if="clasificacionItem.itc">{{ clasificacionItem.itc }}</dd>
                <template v-for="(s, si) in (clasificacionItem.subtipos || [])">
                  <dt :key="'clst-' + si">Subtipo {{ si + 1 }}</dt>
                  <dd :key="'clsd-' + si">{{ s }}</dd>
                </template>
                <dt v-if="clasificacionItem.estadoTipificacion">Estado tipificación</dt><dd v-if="clasificacionItem.estadoTipificacion">{{ clasificacionItem.estadoTipificacion }}</dd>
              </dl>
              <div>
                <div class="seccion-titulo" style="margin-top:0;">Materias del ERP vinculadas al tipo</div>
                <template v-if="(clasificacionItem.materias || []).length">
                  <span v-for="(m, mi) in clasificacionItem.materias" :key="'clm-' + mi" class="tag">{{ m.codigo }}<template v-if="m.nombre"> · {{ m.nombre }}</template></span>
                </template>
                <div v-else class="text-color-gray" style="font-size:12px;">Sin materias vinculadas.</div>
                <div v-if="clasificacionItem.observaciones" class="note" style="margin-top:14px;">{{ clasificacionItem.observaciones }}</div>
                <div v-if="clasificacionItem.indicacionesIdentificacion" class="note info" style="margin-top:14px;"><b>Indicaciones de identificación:</b> {{ clasificacionItem.indicacionesIdentificacion }}</div>
              </div>
            </div>

            <div class="cobertura-row">
              <span v-if="rform.formData.coberturaCatalogo != null">
                Cobertura catálogo: <strong>{{ pct(rform.formData.coberturaCatalogo) }}</strong>
              </span>
              <span v-if="rform.formData.coberturaObligatorias != null" class="margin-left">
                Obligatorias: <strong :class="{'text-color-red': rform.formData.coberturaObligatorias < 1}">{{ pct(rform.formData.coberturaObligatorias) }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- ---------- EVENTOS POR TIPO (dinámico: una pestaña por tipo, ordenadas por catálogo) ---------- -->
        <div v-show="codigoEventoActivo()">
          <div class="panel-card">
            <h3>{{ nombreTipoEventoActivo() }} <span class="coll">· items_eventos ({{ codigoEventoActivo() }})</span></h3>
            <table class="tabla-mock">
              <thead><tr>
                <th>Fecha</th>
                <th v-for="col in columnasListado(eventosDeTipos([codigoEventoActivo()]))" :key="'evd-h-' + col">{{ etiquetaCampo(col) }}</th>
                <th></th>
              </tr></thead>
              <tbody>
                <tr v-for="(ev, ei) in eventosDeTipos([codigoEventoActivo()])" :key="'evd-' + ei">
                  <td class="num">{{ formatFecha(ev.fecha) }}</td>
                  <td v-for="col in columnasListado(eventosDeTipos([codigoEventoActivo()]))" :key="'evd-c-' + ei + '-' + col" :title="valorEventoTexto(ev, col)">{{ valorEventoTexto(ev, col) }}</td>
                  <td class="text-align-center"><f7-link icon-f7="info_circle" @click="abrirDetalleEvento(ev)">Detalle</f7-link></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ---------- INSPECCIONES ---------- -->
        <div v-show="tabActiva === 'inspecciones'">
          <div class="panel-card">
            <h3>Inspecciones <span class="coll">· items_eventos (INSPECCION) · sincronizadas desde expediente</span></h3>
            <p class="h-desc">Inspecciones por Organismo de Control. Cada inspección proviene de un expediente firmado, con su servicio, materia e inspectores.</p>
            <div class="eventos-toolbar">
              <f7-input
                v-if="eventosInspeccionList().length"
                outline
                class="eventos-filtro-input"
                type="text"
                placeholder="Filtrar inspecciones..."
                :value="eventosFiltro"
                @input="eventosFiltro = $event.target.value"
                clear-button
              ></f7-input>
            </div>
            <div class="eventos-tabla-wrap">
              <table class="tabla-mock eventos-tabla">
                <thead>
                  <tr>
                    <th class="th-ordenable" @click="ordenarEventosPor('fecha')">Fecha{{ indicadorOrden('fecha') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('expediente')">Expediente{{ indicadorOrden('expediente') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('cliente')">Cliente{{ indicadorOrden('cliente') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('proximaInspeccion')">Próx. inspección{{ indicadorOrden('proximaInspeccion') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('servicio')">Servicio{{ indicadorOrden('servicio') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('materia')">Materia{{ indicadorOrden('materia') }}</th>
                    <th class="th-ordenable" @click="ordenarEventosPor('inspectores')">Inspectores{{ indicadorOrden('inspectores') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!eventosOrdenadosFiltrados(eventosInspeccionList()).length">
                    <td colspan="8" class="text-color-gray">Sin inspecciones que coincidan con el filtro.</td>
                  </tr>
                  <tr v-for="(ev, ei) in eventosOrdenadosFiltrados(eventosInspeccionList())" :key="'iev-' + ei">
                    <td class="num">{{ formatFecha(ev.fecha) }}</td>
                    <td :title="campoEvento(ev, 'codigoExpediente') || campoEvento(ev, 'idExpediente') || ''">
                      <a
                        v-if="campoEvento(ev, 'idExpediente')"
                        :href="urlVerExpediente(campoEvento(ev, 'idExpediente'))"
                        target="_blank"
                        rel="noopener"
                        class="external evento-expediente-link"
                      ><code>{{ campoEvento(ev, 'codigoExpediente') || campoEvento(ev, 'idExpediente') }}</code> <i class="f7-icons evento-link-icon">arrow_up_right_square</i></a>
                      <code v-else>{{ campoEvento(ev, 'codigoExpediente') || '' }}</code>
                    </td>
                    <td :title="campoEvento(ev, 'razonSocialCliente') || campoEvento(ev, 'codigoCliente') || ''">{{ campoEvento(ev, 'razonSocialCliente') || campoEvento(ev, 'codigoCliente') || '' }}</td>
                    <td class="num">{{ campoEvento(ev, 'proximaInspeccion') ? formatFecha(campoEvento(ev, 'proximaInspeccion')) : '-' }}</td>
                    <td :title="campoEvento(ev, 'servicioNombre') || ''">
                      <a
                        v-if="campoEvento(ev, 'servicioId')"
                        :href="urlVerServicio(campoEvento(ev, 'servicioId'))"
                        target="_blank"
                        rel="noopener"
                        class="external evento-expediente-link"
                      >{{ campoEvento(ev, 'servicioNombre') || campoEvento(ev, 'servicioId') }} <i class="f7-icons evento-link-icon">arrow_up_right_square</i></a>
                    </td>
                    <td :title="formatMateria(campoEvento(ev, 'materia'))">{{ formatMateria(campoEvento(ev, 'materia')) }}</td>
                    <td>
                      <template v-if="!campoEvento(ev, 'inspectores') || !campoEvento(ev, 'inspectores').length"></template>
                      <template v-else-if="campoEvento(ev, 'inspectores').length === 1">{{ formatInspector(campoEvento(ev, 'inspectores')[0]) }}</template>
                      <f7-link
                        v-else
                        popover-open=".popover-inspectores"
                        @click="inspectoresPopoverList = campoEvento(ev, 'inspectores')"
                      >{{ campoEvento(ev, 'inspectores').length }} inspectores</f7-link>
                    </td>
                    <td class="text-align-center"><f7-link icon-f7="info_circle" @click="abrirDetalleEvento(ev)">Detalle</f7-link></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-2 margin-top-half">
              <dl class="dl">
                <dt>Próxima inspección</dt>
                <dd>
                  <span class="num kv-hl" v-if="rform.formData.proximaInspeccion">{{ formatFecha(rform.formData.proximaInspeccion) }}</span>
                  <span v-else>—</span>
                  <span v-if="rform.formData.proximaInspeccionManual" class="prox-insp-badge prox-insp-manual">manual</span>
                  <span v-else-if="rform.formData.proximaInspeccion" class="prox-insp-badge prox-insp-auto">automática</span>
                </dd>
                <dt>Última inspección</dt>
                <dd class="num">{{ rform.formData.fechaUltimaInspeccion ? formatFecha(rform.formData.fechaUltimaInspeccion) : '—' }}</dd>
              </dl>
              <div></div>
            </div>

            <!-- Servicios derivados de las inspecciones -->
            <div class="margin-top">
              <div class="seccion-titulo">Servicios <span class="coll">· derivados de las inspecciones</span></div>
              <div class="eventos-toolbar">
                <f7-input
                  v-if="serviciosDeEventos(eventosItem).length"
                  outline
                  class="eventos-filtro-input"
                  type="text"
                  placeholder="Filtrar servicios..."
                  :value="serviciosFiltro"
                  @input="serviciosFiltro = $event.target.value"
                  clear-button
                ></f7-input>
              </div>
              <table v-if="serviciosDeEventos(eventosItem).length" class="tabla-mock">
                <thead>
                  <tr>
                    <th class="th-ordenable" @click="ordenarServiciosPor('servicio')">Servicio{{ indicadorOrdenServicios('servicio') }}</th>
                    <th class="th-ordenable" @click="ordenarServiciosPor('materias')">Materia{{ indicadorOrdenServicios('materias') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!serviciosOrdenadosFiltrados(eventosItem).length">
                    <td colspan="2" class="text-color-gray">Sin servicios que coincidan con el filtro.</td>
                  </tr>
                  <tr v-for="(s, si) in serviciosOrdenadosFiltrados(eventosItem)" :key="'iesv-' + si">
                    <td :title="s.servicioNombre || ''">
                      <a
                        v-if="s.servicioId"
                        :href="urlVerServicio(s.servicioId)"
                        target="_blank"
                        rel="noopener"
                        class="external evento-expediente-link"
                      >{{ s.servicioNombre || s.servicioId }} <i class="f7-icons evento-link-icon">arrow_up_right_square</i></a>
                    </td>
                    <td :title="s.materiasTexto">{{ s.materiasTexto }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-color-gray padding-half">Sin servicios.</div>
            </div>
          </div>
        </div>

        <!-- ---------- CARACTERÍSTICAS TÉCNICAS ---------- -->
        <div v-show="tabActiva === 'tecnicas'">
          <div class="panel-card">
            <h3>Características técnicas <span class="coll">· caracteristicas</span>
              <f7-link v-if="!rform.readonly && id && !editandoCaract && hasMapContent(rform.formData.caracteristicas)" class="margin-left" icon-f7="square_pencil" @click="iniciarEdicionCaract">Editar</f7-link>
            </h3>
            <p class="h-desc">Datos del item consolidados desde expedientes. Cada valor guarda su fuente e historial.</p>
            <div v-if="hasMapContent(rform.formData.caracteristicas)">
              <div v-for="(grupo, gi) in gruposConCaracteristicas(rform.formData)" :key="'iegr-' + gi" class="grupo-bloque">
                <div class="grupo-header"><strong>{{ grupo.denominacion }}</strong></div>
                <div class="char-grid">
                  <div
                    v-for="c in grupo.items"
                    :key="'iec-' + c.nombre"
                    class="char"
                    :title="c.nombre + (c.nombreOriginalExpediente ? ' · alias: ' + c.nombreOriginalExpediente : '') + (c.fuente ? ' · ' + c.fuente : '')"
                  >
                    <div class="c-k">{{ c.label || c.nombre }}</div>
                    <!-- Las tablas no se vuelcan aquí: su valor crudo es una matriz JSON o el
                         HTML del que salen los informes, y ocuparía toda la ficha. Botón y popup. -->
                    <div v-if="esTablaCaract(c)" class="c-v">
                      <f7-link @click="abrirTablaCaract(c)">
                        <i class="f7-icons" style="font-size:14px;vertical-align:-2px;">table</i>
                        Ver tabla
                      </f7-link>
                      <span class="text-color-gray" style="font-size:10px;margin-left:6px;">{{ c.tipoCaracteristica }}</span>
                    </div>
                    <div v-else-if="!editandoCaract" class="c-v">{{ c.valorActual != null && c.valorActual !== '' ? c.valorActual : '—' }}</div>
                    <input v-else class="completar-select campo-inline" type="text" v-model="caractEdit[c.nombre]" />
                    <div v-if="!editandoCaract && c.historico && c.historico.length" class="c-hist">
                      <f7-link @click="abrirHistoricoCaract(c)"><i class="f7-icons" style="font-size:12px;vertical-align:-1px;">clock</i> Histórico ({{ c.historico.length }})</f7-link>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="editandoCaract" class="doc-actionbar margin-top">
                <f7-button fill color="blue" @click="guardarCaract">Guardar cambios</f7-button>
                <f7-button color="gray" @click="editandoCaract = false">Cancelar</f7-button>
              </div>
            </div>
            <div v-else class="text-color-gray padding-half">Sin características.</div>
          </div>
        </div>

        <!-- ---------- TITULARIDAD Y ROLES ---------- -->
        <div v-show="tabActiva === 'roles'">
          <div class="panel-card">
            <h3>Titularidad y roles <span class="coll">· items_roles</span>
              <f7-link v-if="!rform.readonly && id" class="margin-left" icon-f7="plus_circle" @click="mostrarFormRol = !mostrarFormRol">Añadir / cambiar rol</f7-link>
            </h3>
            <p class="h-desc">Roles vigentes del item (titular, contratante, instaladora, mantenedora, organismo de control). El rol con fecha de baja vacía es el vigente.</p>

            <!-- Alta / cambio manual de un rol -->
            <div v-if="mostrarFormRol && !rform.readonly" class="rol-form">
              <div class="rol-form-grid">
                <div><label class="rol-lbl">Rol</label>
                  <select class="completar-select" v-model="nuevoRol.rol">
                    <option value="">—</option>
                    <option value="TITULAR">Titular</option>
                    <option value="CONTRATANTE">Contratante OCA</option>
                    <option value="EMPRESA_INSTALADORA">Empresa instaladora</option>
                    <option value="EMPRESA_MANTENEDORA">Empresa mantenedora</option>
                    <option value="ORGANISMO_CONTROL">Organismo de control</option>
                  </select>
                </div>
                <div><label class="rol-lbl">Entidad</label><input class="completar-select" type="text" v-model="nuevoRol.razonSocial" placeholder="Razón social" /></div>
                <div><label class="rol-lbl">NIF / CIF</label><input class="completar-select" type="text" v-model="nuevoRol.nif" /></div>
                <div><label class="rol-lbl">Habilitación</label><input class="completar-select" type="text" v-model="nuevoRol.habilitacion" placeholder="Nº habilitación / ENAC" /></div>
              </div>
              <div class="rol-form-actions">
                <f7-button fill small color="blue" @click="guardarRolManual">Guardar rol</f7-button>
                <f7-button small color="gray" @click="mostrarFormRol = false">Cancelar</f7-button>
              </div>
            </div>

            <table class="tabla-mock">
              <thead><tr><th>Rol</th><th>Entidad</th><th>CIF / Habilitación</th><th>Desde</th><th>Estado</th><th>Origen</th><th v-if="!rform.readonly"></th></tr></thead>
              <tbody>
                <tr v-if="!rolesVigentes().length"><td :colspan="rform.readonly ? 6 : 7" class="text-color-gray">Sin roles registrados.</td></tr>
                <tr v-for="(r, ri) in rolesVigentes()" :key="'rolv-' + ri">
                  <td><b>{{ r.rolNombre }}</b></td>
                  <td>{{ r.razonSocial || '—' }}</td>
                  <td class="mono">{{ r.nif || r.habilitacion || '—' }}</td>
                  <td class="num">{{ r.fechaDesde ? formatFecha(r.fechaDesde) : '—' }}</td>
                  <td><span class="st st-cur"><span class="dot"></span>Vigente</span></td>
                  <td>{{ origenRolTexto(r) }}</td>
                  <td v-if="!rform.readonly" class="text-align-center"><f7-link color="red" @click="cerrarRol(r)">Cerrar</f7-link></td>
                </tr>
              </tbody>
            </table>

            <h3 class="margin-top">Histórico de cambios <span class="coll">· roles cerrados</span></h3>
            <p class="h-desc">Roles que dejaron de estar vigentes, con la fecha del cambio y de dónde salió.</p>
            <table v-if="rolesHistoricos().length" class="tabla-mock">
              <thead><tr><th>Rol</th><th>Entidad</th><th>CIF / Habilitación</th><th>Desde</th><th>Hasta</th><th>Estado</th><th>Origen</th></tr></thead>
              <tbody>
                <tr v-for="(r, ri) in rolesHistoricos()" :key="'rolh-' + ri" class="fila-historica">
                  <td>{{ r.rolNombre }}</td>
                  <td>{{ r.razonSocial || '—' }}</td>
                  <td class="mono">{{ r.nif || r.habilitacion || '—' }}</td>
                  <td class="num">{{ r.fechaDesde ? formatFecha(r.fechaDesde) : '—' }}</td>
                  <td class="num">{{ r.fechaHasta ? formatFecha(r.fechaHasta) : '—' }}</td>
                  <td><span class="st" :class="r.estado === 'CERRADO' ? 'st-bad' : 'st-clo'">{{ r.estado === 'CERRADO' ? 'Cerrado' : 'Histórico' }}</span></td>
                  <td>{{ origenRolTexto(r) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-color-gray padding-half">Sin cambios registrados todavía.</div>
          </div>
        </div>

        <!-- ---------- UBICACIÓN ---------- -->
        <div v-show="tabActiva === 'ubicacion'">
          <div class="panel-card">
            <h3>Ubicación <span class="coll">· emplazamiento</span></h3>
            <p class="h-desc">Emplazamiento del item{{ rform.formData.naturaleza === 'FIJ' ? ' (instalación fija: la dirección es parte de la identidad)' : '' }}.</p>
            <div class="col-2">
              <dl class="dl">
                <dt>Dirección</dt><dd>{{ (rform.formData.emplazamiento && rform.formData.emplazamiento.direccion) || '—' }}</dd>
                <dt>CP / Población</dt><dd>{{ [(rform.formData.emplazamiento && rform.formData.emplazamiento.cp), (rform.formData.emplazamiento && rform.formData.emplazamiento.poblacion)].filter(Boolean).join(' · ') || '—' }}</dd>
                <dt>Provincia</dt><dd>{{ (rform.formData.emplazamiento && rform.formData.emplazamiento.provincia) || '—' }}</dd>
                <dt>Comunidad</dt><dd>{{ (rform.formData.emplazamiento && rform.formData.emplazamiento.comunidad) || '—' }}</dd>
              </dl>
              <dl class="dl">
                <dt>Última inspección en</dt><dd>{{ formatDireccion(rform.formData.direccionUltimaInspeccion) || '—' }}</dd>
                <dt>Fecha última inspección</dt><dd class="num">{{ rform.formData.fechaUltimaInspeccion ? formatFecha(rform.formData.fechaUltimaInspeccion) : '—' }}</dd>
              </dl>
            </div>

            <!-- Mapa: con coordenadas si las hay y, si no, buscando la dirección normalizada.
                 Con output=embed no hace falta API key. -->
            <iframe
              v-if="urlMapa(rform.formData.emplazamiento)"
              :src="urlMapa(rform.formData.emplazamiento)"
              class="mapa-emplazamiento"
              frameborder="0"
              loading="lazy"
              allowfullscreen
            ></iframe>
            <div v-else class="text-color-gray" style="font-size:12px;margin-top:8px;">
              Sin coordenadas ni dirección normalizada: no se puede situar en el mapa.
            </div>
          </div>

          <!-- Otros items del mismo sitio: un emplazamiento suele tener varias instalaciones
               de reglamentos distintos (gasolinera = REBT + RITE + IP), cada una su item. -->
          <div class="panel-card">
            <h3>Otros items en este lugar <span class="coll">· mismo emplazamiento</span></h3>
            <p class="h-desc">
              Items que comparten emplazamiento con este. Un mismo sitio suele tener varias
              instalaciones sujetas a reglamentos distintos, y cada una es un item con su propia
              clasificación e inspecciones.
            </p>
            <div v-if="mismoLugarCargando" class="text-color-gray" style="font-size:12px;">Buscando…</div>
            <div v-else-if="!(rform.formData.emplazamiento && rform.formData.emplazamiento.placeId)"
                 class="text-color-gray" style="font-size:12px;">
              Este item no tiene la ubicación normalizada, así que no se puede agrupar por lugar.
            </div>
            <div v-else-if="!mismoLugar.length" class="text-color-gray" style="font-size:12px;">
              No hay otros items en este emplazamiento.
            </div>
            <table v-else class="tabla-mock" v-cell-titles>
              <thead>
                <tr>
                  <th>Item</th><th>Tipo</th><th>Subtipos</th><th>Nat.</th>
                  <th>Estado</th><th>Últ. inspección</th><th>Próxima</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in mismoLugar" :key="'ml-'+m.id" class="fila-clicable"
                    @click="irAItem(m.id, $event)" @click.middle="irAItem(m.id, $event)"
                    v-tooltip="'Ctrl+clic (o clic central) para abrirlo en otra pestaña'">
                  <td>{{ m.id }}</td>
                  <td><span class="badge color-blue" style="font-size:10px;">{{ m.tipo }}</span></td>
                  <td>{{ (m.subtipos || []).join(' / ') || '—' }}</td>
                  <td>
                    <span class="badge" :class="m.naturaleza === 'MOV' ? 'color-orange' : 'color-gray'"
                          style="font-size:10px;"
                          :title="m.naturaleza === 'MOV' ? 'Item móvil: aquí es donde se inspeccionó por última vez, no dónde está' : 'Instalación fija'">
                      {{ m.naturaleza || '—' }}
                    </span>
                  </td>
                  <td>{{ m.estado }}</td>
                  <td class="num">{{ m.fechaUltimaInspeccion ? formatFecha(m.fechaUltimaInspeccion) : '—' }}</td>
                  <td class="num">{{ m.proximaInspeccion ? formatFecha(m.proximaInspeccion) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ---------- INCORPORAR DOCUMENTACIÓN (IA) ---------- -->
        <div v-show="tabActiva === 'incorporar'">
          <div class="panel-card">
            <h3>Incorporar documentación al item <span class="coll">· asistido por IA · items_documentos</span></h3>
            <p class="h-desc">Sueltas un documento del item y la IA lo <b>clasifica</b> contra el catálogo de tipos de evento, <b>extrae</b> sus datos y <b>propone el hito</b> del ciclo de vida. Nada se aplica sin tu confirmación.</p>
            <div class="pipe">
              <div class="pstep"><div class="pn">1</div><div class="pt">Recepción</div><div class="pd">Subida del documento</div></div>
              <div class="pstep"><div class="pn">2</div><div class="pt">Clasificación IA</div><div class="pd">Coteja el tipo con el catálogo</div></div>
              <div class="pstep"><div class="pn">3</div><div class="pt">Extracción</div><div class="pd">Lee los campos del tipo (o propone nuevos)</div></div>
              <div class="pstep"><div class="pn">4</div><div class="pt">Propuesta y archivado</div><div class="pd">Confirmas y nace el evento</div></div>
            </div>

            <!-- Selector de fichero: arrastrar y soltar o clic en la caja -->
            <div
              v-if="!docAnalizando && !docPropuesta"
              class="doc-drop"
              :class="{ 'doc-drop-over': docDragOver }"
              @click="id && $refs.inputDoc.click()"
              @dragover.prevent="docDragOver = true"
              @dragenter.prevent="docDragOver = true"
              @dragleave.prevent="docDragOver = false"
              @drop.prevent="onDropDoc"
            >
              <input type="file" ref="inputDoc" style="display:none;" accept=".pdf,.jpg,.jpeg,.png,.webp" @change="onFicheroDocChange" />
              <div class="doc-drop-titulo">Arrastra aquí el documento del item, o haz clic (PDF o imagen)</div>
              <div class="doc-drop-sub">Proyectos, certificados de instalación, actas de mantenimiento, contratos, informes de inspección, comunicaciones a Industria…</div>
              <f7-button fill class="doc-drop-btn" :disabled="!id" @click.stop="$refs.inputDoc.click()">Seleccionar y analizar</f7-button>
              <div v-if="!id" class="text-color-gray" style="font-size:12px; margin-top:6px;">Guarda el item antes de incorporar documentos.</div>
            </div>

            <r-ia-pensando v-if="docAnalizando" :narracion="docNarracion" latin-default="Reading" texto-default="Analizando el documento…" />

            <div v-if="docError" class="note">⚠ {{ docError }}</div>

            <!-- Propuesta pendiente de revisar -->
            <div v-if="docPropuesta && docPropuesta.propuesta" class="doc-analisis">
              <div class="an-head">
                <div style="min-width:0;">
                  <div style="font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ nombreDocumento(docPropuesta.documento) }}</div>
                  <div style="font-size:12px;color:#5b6b7c;">{{ docPropuesta.propuesta.resumen }}</div>
                </div>
                <div style="text-align:right; flex:0 0 auto;">
                  <div style="font-size:11.5px;color:#5b6b7c;">Tipo detectado</div>
                  <div style="font-weight:700;color:#6b4fa0;">{{ docPropuesta.propuesta.tipoNombre || docPropuesta.propuesta.tipoCodigo }} <span v-if="docPropuesta.propuesta.tipoEsNuevo" class="badge b-warn">nuevo</span></div>
                  <div v-if="docPropuesta.propuesta.confianza != null" style="font-size:11.5px;color:#5b6b7c;">confianza {{ Math.round(docPropuesta.propuesta.confianza * 100) }}%</div>
                </div>
              </div>
              <div class="an-body">
                <dl class="dl" style="max-width:560px;">
                  <dt>Tipo de evento</dt>
                  <dd>
                    <select class="completar-select" v-model="docTipoCorregido" @change="onCambioTipoPropuesta">
                      <option value="">{{ (docPropuesta.propuesta.tipoNombre || docPropuesta.propuesta.tipoCodigo) }} — propuesto por la IA{{ docPropuesta.propuesta.tipoEsNuevo ? ' (tipo nuevo)' : '' }}</option>
                      <option v-for="t in tiposEventoCatalogo" :key="'tec-' + t.codigo" :value="t.codigo">{{ t.codigo }} — {{ t.nombre }}</option>
                      <option value="__NUEVO__">➕ Crear un tipo nuevo…</option>
                    </select>
                  </dd>
                  <template v-if="docTipoCorregido === '__NUEVO__'">
                    <dt>Nombre del tipo nuevo</dt>
                    <dd><input class="completar-select" type="text" v-model="docNuevoNombre" placeholder="p. ej. Memoria de regularización" /></dd>
                  </template>
                  <template v-if="(docTipoCorregido === '' && docPropuesta.propuesta.tipoEsNuevo) || docTipoCorregido === '__NUEVO__'">
                    <dt>Descripción del tipo</dt>
                    <dd><textarea class="completar-select" rows="2" v-model="docTipoDescripcion" placeholder="Qué documentos pertenecen a este tipo (lo usa la IA para clasificar)"></textarea></dd>
                  </template>
                  <dt>Fecha del evento</dt>
                  <dd class="num">{{ docPropuesta.propuesta.fecha ? formatFecha(docPropuesta.propuesta.fecha) : '—' }}</dd>
                </dl>
                <table class="tabla-mock margin-top-half">
                  <thead><tr><th style="width:26%;">Campo</th><th>Valor</th><th style="width:130px;">Ámbito</th><th style="width:36px;"></th></tr></thead>
                  <tbody>
                    <tr v-if="!propuestaCampos.length">
                      <td colspan="4" class="text-color-gray">La IA no extrajo campos del documento.</td>
                    </tr>
                    <tr v-for="(c, ci) in propuestaCampos" :key="'pc-' + ci">
                      <td :title="c.nombre">
                        <input v-if="c.manual" class="completar-select campo-inline" type="text" v-model="c.etiqueta" placeholder="Nombre del campo" />
                        <template v-else>{{ c.etiqueta }} <span v-if="c.esNuevo" class="badge b-warn">nuevo</span></template>
                      </td>
                      <td><input class="completar-select campo-inline" type="text" v-model="c.valor" /></td>
                      <td>
                        <select class="completar-select campo-inline" v-model="c.ambito">
                          <option value="LISTADO">Listado</option>
                          <option value="DETALLE">Solo detalle</option>
                        </select>
                      </td>
                      <td class="text-align-center"><f7-link color="red" icon-f7="trash" @click="propuestaCampos.splice(ci, 1)"></f7-link></td>
                    </tr>
                  </tbody>
                </table>
                <f7-link icon-f7="plus_circle" class="margin-top-half" @click="anadirCampoPropuesta">Añadir campo</f7-link>

                <div v-if="caractActualizadas.length" class="margin-top">
                  <div style="font-weight:600;margin:8px 0 4px;">Características técnicas que se actualizan <span class="coll">· la IA detectó cambios en el documento</span></div>
                  <table class="tabla-mock">
                    <thead><tr><th style="width:40px;"></th><th>Característica</th><th>Valor actual</th><th>Valor nuevo</th></tr></thead>
                    <tbody>
                      <tr v-for="(c, ci) in caractActualizadas" :key="'ca-' + ci">
                        <td class="text-align-center"><input type="checkbox" v-model="c.aplicar" /></td>
                        <td :title="c.nombre">{{ c.label }}</td>
                        <td class="text-color-gray">{{ c.valorAnterior }}</td>
                        <td><b>{{ c.valorNuevo }}</b></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="celdasActualizadas.length" class="margin-top">
                  <div style="font-weight:600;margin:8px 0 4px;">Celdas de tablas que se rellenan <span class="coll">· en las tablas solo se cambia la celda, no la tabla entera</span></div>
                  <table class="tabla-mock">
                    <thead><tr><th style="width:40px;"></th><th>Tabla</th><th>Celda</th><th>Valor actual</th><th>Valor nuevo</th></tr></thead>
                    <tbody>
                      <tr v-for="(c, ci) in celdasActualizadas" :key="'ce-' + ci">
                        <td class="text-align-center"><input type="checkbox" v-model="c.aplicar" /></td>
                        <td :title="c.nombre">{{ c.label }}</td>
                        <td class="text-color-gray">fila {{ c.fila }}, col {{ c.columna }}</td>
                        <td class="text-color-gray">{{ c.valorAnterior }}</td>
                        <td><b>{{ c.valorNuevo }}</b></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="doc-actionbar">
                  <f7-button fill color="blue" @click="aceptarPropuestaTalCual">Lo acepto tal cual</f7-button>
                  <f7-button outline color="blue" @click="guardarCorreccionPropuesta">Guardar mis correcciones…</f7-button>
                  <f7-button outline color="red" @click="descartarPropuesta">Descartar…</f7-button>
                </div>
                <div class="doc-nota-motivo">
                  <b>Lo acepto tal cual</b> aplica la propuesta de la IA como está —incluidos los
                  campos, las características y las celdas de arriba— y no pide explicación.
                  Si has cambiado algo, o has desmarcado alguna característica, usa
                  <b>Guardar mis correcciones</b>: ahí sí hay que decir por qué, porque es lo que
                  permite que la IA mejore.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ---------- DOCUMENTACIÓN ---------- -->
        <div v-show="tabActiva === 'documentacion'">
          <div class="panel-card">
            <h3>Documentación <span class="coll">· items_documentos</span></h3>
            <p class="h-desc">Dossier documental del item: documentos incorporados con IA y copias de los documentos de sus expedientes de origen y de las líneas de pedido que los aportaron.</p>
            <table v-if="documentosItem.length" class="tabla-mock">
              <thead><tr><th>Documento</th><th>Tipo de evento</th><th>Fecha</th><th>Origen</th><th>Estado</th><th v-if="!rform.readonly"></th></tr></thead>
              <tbody>
                <tr v-for="(d, di) in documentosItem" :key="'doc-' + di" :title="(d.propuesta && d.propuesta.resumen) || ''">
                  <td>
                    <a :href="urlDescargaDocumento(d)" target="_blank" rel="noopener" class="external evento-expediente-link">{{ nombreDocumento(d.documento) }} <i class="f7-icons evento-link-icon">arrow_down_doc</i></a>
                  </td>
                  <td>{{ tipoEventoDeDocumento(d) }}</td>
                  <td class="num">{{ d.fecha ? formatFecha(d.fecha) : '—' }}</td>
                  <td>{{ labelOrigenDoc(d.origen) }}</td>
                  <td><span class="st" :class="claseEstadoDoc(d.estado)">{{ d.estado }}</span></td>
                  <td v-if="!rform.readonly" class="text-align-center">
                    <f7-link v-if="d.origen === 'SUBIDO'" color="red" @click="eliminarDocumento(d)">Eliminar</f7-link>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-color-gray padding-half">Sin documentos.</div>
          </div>
        </div>

        <!-- ---------- TRAZABILIDAD ---------- -->
        <div v-show="tabActiva === 'trazabilidad'">
          <div class="panel-card">
            <h3>Trazabilidad del ciclo de vida <span class="coll">· eventos + roles</span></h3>
            <p class="h-desc">Todos los hitos del item (eventos y cambios de rol) ordenados en el tiempo. Pulsa un hito para ver su detalle.</p>
            <div v-if="timelineHitos().length || rform.formData.proximaInspeccion" class="tl">
              <div v-for="(h, hi) in timelineHitos()" :key="'tl-' + hi" class="tl-item tl-click" :class="h.color" @click="clickHito(h)">
                <div class="tl-date">{{ formatFecha(h.fecha) }}</div>
                <div class="tl-title">{{ h.titulo }}</div>
                <div v-if="h.desc" class="tl-desc">{{ h.desc }}</div>
              </div>
              <div v-if="rform.formData.proximaInspeccion" class="tl-item">
                <div class="tl-date">{{ formatFecha(rform.formData.proximaInspeccion) }} · previsto</div>
                <div class="tl-title">Próxima inspección periódica</div>
                <div class="tl-desc">{{ rform.formData.proximaInspeccionManual ? 'Fijada a mano en la ficha.' : 'Calculada desde la inspección más reciente.' }}</div>
              </div>
            </div>
            <div v-else class="text-color-gray padding-half">Sin hitos.</div>
          </div>
        </div>

        <!-- ---------- AUDITORÍA ---------- -->
        <div v-show="tabActiva === 'auditoria'">
          <div class="panel-card">
            <h3>Auditoría <span class="coll">· metadatos del registro</span></h3>
            <p class="h-desc">Trazabilidad técnica de creación y modificación del registro del item.</p>
            <div class="col-2">
              <dl class="dl">
                <dt>Creado</dt><dd class="num">{{ rform.formData.fechaCreacion ? formatFecha(rform.formData.fechaCreacion) : '—' }}</dd>
                <dt v-if="rform.formData.creationUserName">Creado por</dt><dd v-if="rform.formData.creationUserName" class="mono">{{ rform.formData.creationUserName }}</dd>
                <dt>Última actualización</dt><dd class="num">{{ rform.formData.fechaUltimaActualizacion ? formatFecha(rform.formData.fechaUltimaActualizacion) : '—' }}</dd>
                <dt v-if="rform.formData.modificationUserName">Modificado por</dt><dd v-if="rform.formData.modificationUserName" class="mono">{{ rform.formData.modificationUserName }}</dd>
              </dl>
              <dl class="dl">
                <dt>Nº de eventos</dt><dd>{{ eventosItem.length }}</dd>
                <dt>Expedientes vinculados</dt><dd>{{ (rform.formData.expedientesVinculados || []).length }}</dd>
                <dt v-if="rform.formData.coberturaCatalogo != null">Cobertura catálogo</dt><dd v-if="rform.formData.coberturaCatalogo != null">{{ pct(rform.formData.coberturaCatalogo) }}</dd>
                <dt v-if="rform.formData.coberturaObligatorias != null">Cobertura obligatorias</dt><dd v-if="rform.formData.coberturaObligatorias != null">{{ pct(rform.formData.coberturaObligatorias) }}</dd>
              </dl>
            </div>
          </div>
        </div>

        <!-- Popover compartido: lista de inspectores de un evento con más de uno -->
        <f7-popover class="popover-inspectores">
          <f7-list>
            <f7-list-item
              v-for="(insp, ii) in inspectoresPopoverList"
              :key="'insp-' + ii"
              :title="formatInspector(insp)"
            ></f7-list-item>
          </f7-list>
        </f7-popover>

        <!-- Popup de detalle de un evento: totalizado de todos sus campos -->
        <f7-popup class="popup-evento-detalle" :opened="eventoDetalle != null" @popup:closed="eventoDetalle = null; editandoEvento = false">
          <f7-page v-if="eventoDetalle">
            <f7-navbar :title="(eventoDetalle.tipo && eventoDetalle.tipo.nombre) || 'Evento'"></f7-navbar>
            <div class="panel-card">
              <!-- Lectura -->
              <template v-if="!editandoEvento">
                <dl class="dl">
                  <dt>Tipo</dt><dd>{{ (eventoDetalle.tipo && eventoDetalle.tipo.nombre) || '—' }}</dd>
                  <dt>Fecha</dt><dd>{{ eventoDetalle.fecha ? formatFecha(eventoDetalle.fecha) : '—' }}</dd>
                  <template v-for="c in camposTotalizado(eventoDetalle)">
                    <dt :key="'evd-dt-' + c.clave">{{ c.etiqueta }}</dt>
                    <dd :key="'evd-dd-' + c.clave">
                      {{ c.valor || '—' }}
                      <span v-if="edicionCampo(eventoDetalle, c.clave)" class="badge b-edit"
                        :title="'Editado por ' + edicionCampo(eventoDetalle, c.clave).usuario + ' el ' + formatFecha(edicionCampo(eventoDetalle, c.clave).fecha)">
                        ✎ {{ edicionCampo(eventoDetalle, c.clave).usuario }}
                      </span>
                    </dd>
                  </template>
                </dl>
                <p v-if="!camposTotalizado(eventoDetalle).length" class="h-desc">Este evento no tiene campos registrados.</p>
                <div v-if="documentosDeEvento(eventoDetalle).length" class="margin-top">
                  <div style="font-weight:600;margin-bottom:4px;">Documento asociado</div>
                  <div v-for="(d, di) in documentosDeEvento(eventoDetalle)" :key="'evd-doc-' + di">
                    <a :href="urlDescargaDocumento(d)" target="_blank" rel="noopener" class="external evento-expediente-link">
                      {{ nombreDocumento(d.documento) }} <i class="f7-icons evento-link-icon">arrow_down_doc</i>
                    </a>
                  </div>
                </div>
                <div v-if="!rform.readonly && eventoBorrable(eventoDetalle)" class="margin-top doc-actionbar">
                  <f7-button fill color="blue" @click="iniciarEdicionEvento">Editar campos</f7-button>
                  <f7-button outline color="red" @click="eliminarEventoDetalle(eventoDetalle)">Eliminar evento</f7-button>
                </div>
              </template>

              <!-- Edición de campos -->
              <template v-else>
                <table class="tabla-mock">
                  <thead><tr><th style="width:26%;">Campo</th><th>Valor</th><th style="width:130px;">Ámbito</th><th style="width:36px;"></th></tr></thead>
                  <tbody>
                    <tr v-for="(c, ci) in edicionCampos" :key="'edc-' + ci">
                      <td :title="c.nombre">
                        <input v-if="c.manual" class="completar-select campo-inline" type="text" v-model="c.etiqueta" placeholder="Nombre del campo" />
                        <template v-else>{{ c.etiqueta }}</template>
                      </td>
                      <td><input class="completar-select campo-inline" type="text" v-model="c.valor" /></td>
                      <td>
                        <select class="completar-select campo-inline" v-model="c.ambito">
                          <option value="LISTADO">Listado</option>
                          <option value="DETALLE">Solo detalle</option>
                        </select>
                      </td>
                      <td class="text-align-center"><f7-link color="red" icon-f7="trash" @click="edicionCampos.splice(ci, 1)"></f7-link></td>
                    </tr>
                  </tbody>
                </table>
                <f7-link icon-f7="plus_circle" class="margin-top-half" @click="anadirCampoEdicion">Añadir campo</f7-link>
                <div class="margin-top doc-actionbar">
                  <f7-button fill color="blue" @click="guardarEdicionEvento">Guardar cambios</f7-button>
                  <f7-button color="gray" @click="editandoEvento = false">Cancelar</f7-button>
                </div>
              </template>
            </div>
          </f7-page>
        </f7-popup>

        <!-- Popup: histórico de valores de una característica -->
        <!-- Visor de tablas: TABLA y TABLA_DINAMICA se pintan desde las filas; la
             TABLA_PERSONALIZADA se muestra con su HTML original, que es lo que sale en el
             informe PDF (celdas combinadas y estilos incluidos). -->
        <f7-popup class="popup-caract-tabla" :opened="tablaCaract != null" @popup:closed="tablaCaract = null">
          <f7-page v-if="tablaCaract">
            <!-- Sin hijos, como los demás popups de esta ficha: app.js mete los suyos (maximizar y
                 cerrar) en el nav-right de todos, y añadir slots aquí deja el título sin pintar. -->
            <f7-navbar :title="tablaCaract.label || tablaCaract.clave"></f7-navbar>
            <div class="panel-card">
              <div class="tabla-cabecera">
                <p class="h-desc">
                  <span class="mono">{{ tablaCaract.clave }}</span> · {{ tablaCaract.tipo }}
                  <span v-if="editandoTabla" class="text-color-gray">
                    · solo se pueden cambiar las celdas de datos; no se añaden filas ni columnas
                  </span>
                </p>
                <div class="tabla-acciones">
                  <f7-button v-if="!editandoTabla && tablaCaract.interpretada && !rform.readonly"
                             small outline color="blue" @click="iniciarEdicionTabla">Editar</f7-button>
                  <f7-button v-if="editandoTabla" small fill color="blue"
                             :class="{ disabled: guardandoTabla }" @click="guardarTabla">
                    {{ guardandoTabla ? 'Guardando…' : 'Guardar' }}
                  </f7-button>
                  <f7-button v-if="editandoTabla" small color="gray" @click="editandoTabla = false">Cancelar</f7-button>
                </div>
              </div>

              <div v-if="tablaCaractCargando" class="text-color-gray">Cargando…</div>

              <!-- Modo edición: siempre por rejilla, también en las personalizadas. Las celdas
                   que el formato marca de solo lectura (el enunciado) salen bloqueadas. -->
              <table v-else-if="editandoTabla" class="tabla-mock tabla-editable">
                <tbody>
                  <tr v-for="(f, fi) in tablaEdit" :key="'te-'+fi">
                    <td v-for="(cel, ci) in f" :key="'tec-'+fi+'-'+ci"
                        :class="{ 'celda-bloqueada': !celdaEditable(fi, ci), 'celda-cabecera': fi === 0 }">
                      <input v-if="celdaEditable(fi, ci)" class="celda-input" type="text" v-model="tablaEdit[fi][ci]" />
                      <span v-else :title="'Celda de solo lectura en el formato'">{{ cel }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-else-if="!tablaCaract.interpretada" class="text-color-gray">
                No se ha podido interpretar el contenido como tabla. Valor en crudo:
                <pre class="tabla-crudo">{{ tablaCaractCrudo }}</pre>
              </div>

              <!-- Personalizada: su propio HTML, tal cual sale en el informe -->
              <div v-else-if="tablaCaract.html" class="tabla-personalizada-wrap" v-html="tablaCaract.html"></div>

              <!-- Matriz: fila 0 = cabeceras -->
              <table v-else class="tabla-mock" v-cell-titles>
                <thead v-if="(tablaCaract.filas || []).length">
                  <tr><th v-for="(h, hi) in tablaCaract.filas[0]" :key="'tth-'+hi">{{ h }}</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(f, fi) in (tablaCaract.filas || []).slice(1)" :key="'ttr-'+fi">
                    <td v-for="(cel, ci) in f" :key="'ttd-'+fi+'-'+ci">{{ cel !== '' ? cel : '—' }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="tablaCaract.interpretada && (tablaCaract.filas || []).length <= 1"
                   class="text-color-gray" style="font-size:12px;margin-top:6px;">
                La tabla no tiene datos, solo las cabeceras.
              </div>
            </div>
          </f7-page>
        </f7-popup>

        <f7-popup class="popup-caract-historico" :opened="caractDetalle != null" @popup:closed="caractDetalle = null">
          <f7-page v-if="caractDetalle">
            <f7-navbar :title="caractDetalle.label || caractDetalle.nombre"></f7-navbar>
            <div class="panel-card">
              <dl class="dl">
                <dt>Valor actual</dt><dd>{{ caractDetalle.valorActual != null && caractDetalle.valorActual !== '' ? caractDetalle.valorActual : '—' }}</dd>
                <dt>Fuente actual</dt><dd>{{ caractDetalle.fuente || '—' }}</dd>
              </dl>
              <h3 class="margin-top">Histórico de valores <span class="coll">· más reciente primero</span></h3>
              <table v-if="(caractDetalle.historico || []).length" class="tabla-mock" v-cell-titles>
                <thead><tr><th>Valor anterior</th><th>Fuente</th><th>Fecha</th><th>Usuario</th></tr></thead>
                <tbody>
                  <tr v-for="(h, hi) in historicoOrdenado(caractDetalle)" :key="'hc-' + hi">
                    <td>{{ h.valor != null && h.valor !== '' ? h.valor : '—' }}</td>
                    <td>{{ h.fuente || '—' }}</td>
                    <td class="num">{{ h.fecha ? formatFecha(h.fecha) : '—' }}</td>
                    <td class="mono">{{ h.username || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-color-gray padding-half">Sin histórico.</div>
            </div>
          </f7-page>
        </f7-popup>

      </div>
    </r-form>
  </f7-page>
</template>

<style scoped>
/* ============ Ficha estilo mockup ficha_item_v2_2 ============ */
.ficha {
  max-width: 1160px;
  margin: 0 auto;
  padding: 14px 14px 50px;
  color: #1c2733;
  font-size: 14px;
}
.mono {
  font-family: "SF Mono", ui-monospace, "Cascadia Code", Consolas, "Courier New", monospace;
}

/* --- Cabecera --- */
.head {
  background: linear-gradient(180deg, #ffffff, #fbfcfe);
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(20, 40, 70, 0.08), 0 8px 24px rgba(20, 40, 70, 0.06);
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
}
.head::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(180deg, #0f766e, #0b5a54);
}
.head-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
}
/* El identificador puede ser larguísimo (hashes de dirección): se limita a ~2/3 del
   ancho con elipsis (completo en el tooltip) para que la columna derecha (clase de
   item / en servicio desde) conserve su tercio al lado, como en el mockup. */
.id-block {
  flex: 1 1 0;
  max-width: 66%;
  min-width: 0;
}
.head-right {
  flex: 0 1 auto;
  max-width: 32%;
  min-width: 0;
}
.id-block .eyebrow {
  font-size: 11.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5b6b7c;
  font-weight: 600;
}
/* Fila valor + icono de copiar: el valor encoge (elipsis), el icono nunca. */
.id-line,
.valor-copiable {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.id-line h1,
.valor-copiable .kv-id {
  flex: 0 1 auto;
  min-width: 0;
}
/* Icono de copiar: flotando al borde derecho del campo, discreto y pequeño (lo justo
   para poder pulsarlo). */
.copy-btn {
  flex: 0 0 auto;
  margin-left: auto;
  color: #90a4ae;
  padding: 2px 4px;
  min-width: 0;
}
.copy-btn:hover { color: #1f3f74; }
.copy-btn .f7-icons { font-size: 12px; }
.id-block h1 {
  margin: 4px 0 6px;
  font-size: 23px;
  letter-spacing: 0.5px;
  color: #1f3f74;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.id-block .sub {
  color: #5b6b7c;
  font-size: 13px;
  max-width: 640px;
}
.badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}
.b-teal { background: #dcf3f0; color: #0f766e; border-color: #bfe6e1; }
.b-slate { background: #eef2f7; color: #64748b; border-color: #dbe3ec; }
.b-ok { background: #e3f5ec; color: #1f8a5b; border-color: #c7ebd7; }
.b-warn { background: #fdf3e0; color: #b7791f; border-color: #f0dcae; }
.b-bad { background: #fbe9e7; color: #c0392b; border-color: #f5c8c2; }
.dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; display: inline-block; }
.head-right {
  text-align: right;
  color: #5b6b7c;
  font-size: 12px;
  overflow-wrap: anywhere;
}
@media (max-width: 640px) {
  .id-block, .head-right {
    max-width: 100%;
    flex-basis: 100%;
  }
  .head-right { text-align: left; }
}
.head-right-label { margin-top: 2px; }
.head-clase {
  font-size: 15px;
  color: #1f3f74;
  font-weight: 700;
  margin-top: 2px;
}
.head-since { font-weight: 700; color: #1c2733; }

/* --- KPIs --- */
.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 16px 0 6px;
}
.kpi {
  background: #fff;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(20, 40, 70, 0.08), 0 8px 24px rgba(20, 40, 70, 0.06);
}
.kpi .k-label {
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5b6b7c;
  font-weight: 600;
}
.kpi .k-val {
  font-size: 19px;
  font-weight: 700;
  margin-top: 5px;
  color: #1c2733;
}
.kpi .k-note {
  font-size: 12px;
  color: #5b6b7c;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.meter {
  height: 7px;
  border-radius: 99px;
  background: #e7edf4;
  margin-top: 10px;
  overflow: hidden;
}
.meter > i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #1f8a5b, #37a86e);
}

/* --- Pestañas --- */
.tabs-mock {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  margin: 20px 0 0;
  border-bottom: 1px solid #dbe2ea;
}
.tab {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #5b6b7c;
  border-bottom: 2.5px solid transparent;
  margin-bottom: -1px;
  border-radius: 7px 7px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: 0.15s;
  font-family: inherit;
  /* El CSS global de la app pone width:100% a los <button>: sin esto cada pestaña
     ocupa una línea entera. */
  width: auto !important;
  flex: 0 0 auto;
  box-sizing: border-box;
  text-transform: none;
  line-height: normal;
  height: auto;
}
.tab:hover { color: #1f3f74; background: #f5f8fc; }
.tab.active { color: #1f3f74; border-bottom-color: #2b579a; }
.tab .pill {
  background: #e7edf4;
  color: #64748b;
  border-radius: 99px;
  font-size: 11px;
  padding: 1px 7px;
  font-weight: 700;
}
.tab.active .pill { background: #eaf1fb; color: #1f3f74; }
.tab .pill-ia { background: #efeafa; color: #6b4fa0; }

/* --- Paneles --- */
.panel-card {
  background: #fff;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(20, 40, 70, 0.08), 0 8px 24px rgba(20, 40, 70, 0.06);
  padding: 18px 20px;
  margin-top: 16px;
}
.panel-card h3 {
  margin: 0 0 4px;
  font-size: 15px;
  color: #1f3f74;
}
.panel-card .h-desc {
  color: #5b6b7c;
  font-size: 12.5px;
  margin: 0 0 14px;
}
.coll { font-size: 11.5px; color: #0f766e; font-weight: 600; }
.col-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
}
.seccion-titulo {
  font-size: 13.5px;
  font-weight: 700;
  color: #1f3f74;
  margin: 6px 0;
}

/* --- Definition lists --- */
.dl {
  display: grid;
  /* minmax(0, 1fr) y no 1fr a secas: permite que la celda del valor ENCOJA por debajo
     de su contenido (ids larguísimos con nowrap) en vez de desbordar el contenedor. */
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 0;
}
.dl > dd { min-width: 0; }
.dl > dt {
  padding: 8px 0;
  color: #5b6b7c;
  font-size: 12.5px;
  border-top: 1px solid #dbe2ea;
  font-weight: 600;
}
.dl > dd {
  padding: 8px 0;
  margin: 0;
  border-top: 1px solid #dbe2ea;
  font-size: 13.5px;
  overflow-wrap: anywhere;
}
.dl > dt:first-of-type,
.dl > dd:first-of-type { border-top: none; }
.kv-hl {
  background: #eaf1fb;
  border: 1px solid #d3e2fb;
  color: #1f3f74;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
}
/* ID canónico en una sola línea acotada a su hueco (los hashes de dirección son
   larguísimos): ajustarTamanoId() encoge la fuente hasta 1em para intentar que quepa
   entero; si aún así no entra, elipsis y el valor completo en el tooltip. */
.kv-id {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  font-size: 15px;
}
.tag {
  display: inline-block;
  background: #eef2f7;
  border: 1px solid #dbe2ea;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  margin: 2px 4px 2px 0;
  color: #42546a;
}

/* --- Tablas estilo mockup --- */
.mapa-emplazamiento {
  width: 100%;
  height: 320px;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  margin-top: 12px;
  display: block;
}

.fila-clicable { cursor: pointer; }
.fila-clicable:hover td { background: #eef4ff; }

.tabla-mock {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  /* Acotada al contenedor: reparte columnas y parte valores largos en vez de desbordar
     por la derecha (la tabla de inspecciones mantiene sus anchos/elipsis propios). */
  table-layout: fixed;
}
.tabla-mock thead th,
.tabla-mock tbody td {
  overflow-wrap: anywhere;
}
.tabla-mock thead th {
  text-align: left;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5b6b7c;
  font-weight: 700;
  padding: 9px 12px;
  background: #f6f9fc;
  border-bottom: 1px solid #dbe2ea;
}
.tabla-mock tbody td {
  padding: 9px 12px;
  border-bottom: 1px solid #dbe2ea;
  vertical-align: top;
}
.tabla-mock tbody tr:last-child td { border-bottom: none; }
.tabla-mock tbody tr:hover { background: #f8fbff; }
.num { font-variant-numeric: tabular-nums; }
.fila-historica { color: #5b6b7c; }

/* --- Estados (chips de tabla) --- */
.st {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 12px;
  padding: 3px 9px;
  border-radius: 99px;
  white-space: nowrap;
}
.st-cur { background: #eaf1fb; color: #1f3f74; }
.st-clo { background: #eef2f7; color: #64748b; }
.st-soon { background: #fdf3e0; color: #b7791f; }
.st-bad { background: #fbe9e7; color: #c0392b; }

/* --- Notas --- */
.note {
  background: #fdf3e0;
  border: 1px solid #f0dcae;
  border-left: 3px solid #b7791f;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 12.5px;
  color: #7a5b12;
  margin-top: 16px;
}
.note.info {
  background: #eaf1fb;
  border-color: #cfe0fb;
  border-left-color: #2b579a;
  color: #1f3f74;
}
.note-title { font-weight: 700; margin-bottom: 4px; }
.warning-line { font-size: 12px; padding: 2px 0; }

/* --- Timeline --- */
.tl { position: relative; margin: 6px 0 0; padding-left: 26px; }
.tl::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: #dbe2ea;
}
.tl-item { position: relative; padding: 0 0 16px; }
.tl-item::before {
  content: "";
  position: absolute;
  left: -22px;
  top: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #2b579a;
}
.tl-item.ok::before { border-color: #1f8a5b; }
.tl-item.warn::before { border-color: #b7791f; }
.tl-item.teal::before { border-color: #0f766e; }
.tl-item.mut::before { border-color: #64748b; }
.tl-item.role::before { border-color: #6b4fa0; }
.tl-item.bad::before { border-color: #c0392b; }
.tl-item.tl-click { cursor: pointer; }
.tl-item.tl-click:hover .tl-title { color: #6b4fa0; text-decoration: underline; }
.tl-date { font-size: 12px; color: #5b6b7c; font-weight: 600; }
.tl-title { font-weight: 600; margin: 1px 0; }
.tl-desc { font-size: 12.5px; color: #5b6b7c; }

/* --- Características (grid de tarjetas) --- */
.grupo-bloque { margin: 10px 0 14px; }
.grupo-header {
  margin-bottom: 8px;
  font-size: 13px;
  color: #1f3f74;
}
.char-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.char {
  border: 1px solid #dbe2ea;
  border-radius: 10px;
  padding: 10px 14px;
  background: #fbfcfe;
  min-width: 0;
}
.char .c-k {
  font-size: 12px;
  color: #5b6b7c;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.char .c-v {
  font-size: 18px;
  font-weight: 700;
  margin: 3px 0 2px;
  overflow-wrap: anywhere;
}
.char .c-hist {
  margin-top: 4px;
  font-size: 11px;
}
.char .c-hist a {
  color: #6b4fa0;
}

/* --- Incorporar documentación: drop + panel de propuesta --- */
.doc-drop {
  border: 2px dashed #c9b8ec;
  background: linear-gradient(180deg, #faf8ff, #f3edfc);
  border-radius: 12px;
  padding: 22px;
  text-align: center;
  color: #6b4fa0;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.doc-drop:hover {
  border-color: #9a7fd0;
}
.doc-drop-over {
  border-color: #6b4fa0;
  border-style: solid;
  background: linear-gradient(180deg, #f1eafc, #e7dbfa);
  box-shadow: 0 0 0 3px rgba(107, 79, 160, 0.15);
}
.doc-drop-titulo {
  font-weight: 700;
  color: #1f3f74;
  margin-top: 4px;
}
.doc-drop-sub {
  font-size: 12px;
  color: #5b6b7c;
  margin: 5px auto 0;
  max-width: 560px;
}
.doc-drop-btn {
  display: inline-flex;
  width: auto;
  margin: 14px auto 0;
  background: #6b4fa0;
}
.doc-analisis {
  border: 1px solid #e0d7f5;
  border-radius: 12px;
  margin-top: 18px;
  overflow: hidden;
}
.an-head {
  background: linear-gradient(180deg, #f7f3ff, #efe8fb);
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  border-bottom: 1px solid #e6ddf6;
}
.an-body {
  padding: 14px 16px;
}
.doc-actionbar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.doc-actionbar .button {
  width: auto;
  padding: 0 16px;
}
.doc-nota-motivo {
  font-size: 12px;
  color: #5b6b7c;
  line-height: 1.45;
  margin-top: 8px;
}

/* --- Pipeline IA (Incorporar documentación) --- */
.pipe {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  margin: 6px 0 16px;
}
.pstep { position: relative; padding: 0 14px; text-align: center; }
.pstep .pn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #efeafa;
  color: #6b4fa0;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #e0d7f5;
}
.pstep .pt { font-weight: 600; font-size: 13px; margin-top: 8px; }
.pstep .pd { font-size: 11.5px; color: #5b6b7c; margin-top: 2px; }
.pstep:not(:last-child)::after {
  content: "→";
  position: absolute;
  right: -9px;
  top: 4px;
  color: #c3ccd8;
  font-size: 18px;
}

/* --- Responsive --- */
@media (max-width: 860px) {
  .col-2 { grid-template-columns: 1fr; }
  .kpis { grid-template-columns: 1fr 1fr; }
  .char-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 520px) {
  .kpis { grid-template-columns: 1fr; }
  .char-grid { grid-template-columns: 1fr; }
}
@media (max-width: 720px) {
  .pipe { grid-template-columns: 1fr 1fr; gap: 16px; }
  .pstep::after { display: none !important; }
}

/* ============ Estilos heredados que se conservan ============ */
::v-deep .codigo-readonly .readonly .item-input-wrap input {
  background-color: #e9ecef;
}
.clasif-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}
.chip-tipologia {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin: 1px 2px;
  border-radius: 12px;
  font-size: 11px;
  background: #e8eaee;
  color: #888;
  border: 1px solid transparent;
  user-select: none;
}
.chip-tipologia.chip-activo {
  background: #c8e6c9;
  color: #1b5e20;
  border-color: #81c784;
  font-weight: 600;
}
.chip-tipologia.chip-raiz { border-style: solid; }
.chip-tipologia.chip-pendiente {
  background: #fff3e0;
  color: #e65100;
  border: 1px dashed #ffb74d;
  font-weight: 700;
}
.cobertura-row {
  margin-top: 8px;
  font-size: 12px;
  color: #455a64;
}
.parcial-badge {
  margin-left: 8px;
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffb74d;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.completar-tipologia {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff8f0;
  border: 1px dashed #ffb74d;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.caract-label {
  font-size: 12px;
  font-weight: 600;
  color: #455a64;
}
.completar-select {
  padding: 5px 8px;
  border: 1px solid #ffb74d;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  min-width: 240px;
  max-width: 100%;
  box-sizing: border-box;
}
.rol-form {
  background: #f7f9fc;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.rol-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}
.rol-form-grid .completar-select {
  min-width: 0;
  width: 100%;
}
.rol-lbl {
  display: block;
  font-size: 11px;
  color: #5b6b7c;
  margin-bottom: 2px;
}
.rol-form-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}
@media (min-width: 768px) {
  .rol-form-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.campo-inline {
  min-width: 0;
  width: 100%;
  padding: 4px 6px;
  font-size: 12.5px;
}
.badge.b-edit {
  background: #eaf3ff;
  color: #1565c0;
  border: 1px solid #b6d4f5;
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
  white-space: nowrap;
}
.clasif-link {
  margin-left: 10px;
  font-size: 12px;
  color: #1565c0;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.clasif-link:hover { text-decoration: underline; }
.evento-expediente-link {
  text-decoration: none;
  cursor: pointer;
}
.evento-expediente-link code { color: #1565c0; }
.evento-expediente-link:hover code { text-decoration: underline; }
.evento-link-icon {
  font-size: 11px;
  color: #1565c0;
  vertical-align: middle;
}
.eventos-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}
.eventos-filtro-input {
  max-width: 260px;
  flex: 1;
}
.th-ordenable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-ordenable:hover { color: #1a6fb5; }
/* La tabla de inspecciones no debe desbordar la tarjeta: layout fijo + elipsis por celda,
   con scroll horizontal como último recurso en pantallas muy estrechas. */
.eventos-tabla-wrap {
  width: 100%;
  overflow-x: auto;
}
.eventos-tabla { table-layout: fixed; }
.eventos-tabla th,
.eventos-tabla td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.eventos-tabla th:nth-child(1), .eventos-tabla td:nth-child(1) { width: 9%; }
.eventos-tabla th:nth-child(2), .eventos-tabla td:nth-child(2) { width: 15%; }
.eventos-tabla th:nth-child(3), .eventos-tabla td:nth-child(3) { width: 15%; }
.eventos-tabla th:nth-child(4), .eventos-tabla td:nth-child(4) { width: 11%; }
.eventos-tabla th:nth-child(5), .eventos-tabla td:nth-child(5) { width: 17%; }
.eventos-tabla th:nth-child(6), .eventos-tabla td:nth-child(6) { width: 13%; }
.eventos-tabla th:nth-child(7), .eventos-tabla td:nth-child(7) { width: 12%; }
.eventos-tabla th:nth-child(8), .eventos-tabla td:nth-child(8) { width: 8%; }
.popover-inspectores { width: 280px; }
.observaciones-input ::v-deep textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
}
.prox-insp-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 4px 0 4px;
  font-size: 12px;
}
.prox-insp-badge {
  padding: 1px 8px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
.prox-insp-manual {
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
}
.prox-insp-auto {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

/* --- Visor de tablas de característica --- */
.popup-caract-tabla .tabla-mock { width: 100%; }
/* Los botones van aquí y no en el navbar: ese espacio lo ocupan los que app.js inyecta en todos
   los popups (maximizar y cerrar). */
.tabla-cabecera {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.tabla-cabecera .h-desc { flex: 1; margin: 0; }
.tabla-acciones { display: flex; gap: 6px; flex-shrink: 0; }
.tabla-acciones .button { padding: 0 12px; }
/* El HTML de TABLA_PERSONALIZADA lleva posicionamiento absoluto y anchos fijos: se le da un
   contenedor propio con scroll para que no desborde el popup ni se solape con lo de al lado. */
.tabla-personalizada-wrap {
  position: relative;
  overflow: auto;
  max-height: 65vh;
  padding: 4px;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fff;
}
/* En edición se pinta rejilla también en las personalizadas: el HTML de esas trae sus propias
   celdas contenteditable, y dejar escribir sobre él obligaría a diffear el DOM para saber qué
   cambió. Con la rejilla se manda fila/columna y el servidor reescribe solo esa celda. */
.tabla-editable td { padding: 0 !important; }
.tabla-editable .celda-input {
  width: 100%;
  border: none;
  outline: none;
  background: #fffdf3;
  padding: 6px 8px;
  font-size: 12px;
  font-family: inherit;
}
.tabla-editable .celda-input:focus { background: #fff7d6; }
.tabla-editable .celda-bloqueada {
  background: #f2f4f7;
  color: #667085;
  padding: 6px 8px !important;
  font-size: 12px;
}
.tabla-editable .celda-cabecera .celda-input { font-weight: 600; }
.tabla-crudo {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 10px;
  max-height: 40vh;
  overflow: auto;
  background: #f7f8fa;
  border: 1px solid #e5e9f0;
  border-radius: 5px;
  padding: 6px;
}

</style>

<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rIaPensando from "./../../components/rIaPensando.vue";
import ItemEnriquecido from "./../../js/db/ItemEnriquecido.js";
import ItemClasificacion from "./../../js/db/ItemClasificacion.js";
import ItemDocumento from "./../../js/db/ItemDocumento.js";
import ItemRol from "./../../js/db/ItemRol.js";
import ItemDocumentoEvents from "./../../js/db/ItemDocumentoEvents.js";
import Documento from "./../../js/db/Documento.js";
import cerrojoDeLaSesion from "./../../js/utils/cerrojoDeLaSesion.js";

/**
 * Mínimo de caracteres de un motivo de corrección. El mismo que exige la API (MOTIVO_MINIMO en
 * ItemDocumentoDecisionService): aquí se comprueba para avisar antes de guardar —condición de
 * Dirección—, y allí para que no entre nada sin justificar por otra vía.
 */
const MOTIVO_MINIMO = 5;

/** Pone en cada celda de tabla su texto completo como title (tooltip nativo al pasar el ratón). */
function ponerTitulosCeldas(el) {
  var celdas = el.querySelectorAll('td');
  for (var i = 0; i < celdas.length; i++) {
    var texto = (celdas[i].textContent || '').trim();
    if (texto) {
      if (celdas[i].getAttribute('title') !== texto) celdas[i].setAttribute('title', texto);
    } else if (celdas[i].hasAttribute('title')) {
      celdas[i].removeAttribute('title');
    }
  }
}

export default {
  components: {
    rForm,
    rInput,
    rIaPensando
  },
  directives: {
    cellTitles: {
      inserted: function (el) { ponerTitulosCeldas(el); },
      componentUpdated: function (el) { ponerTitulosCeldas(el); }
    }
  },
  data() {
    return {
      ItemEnriquecido,
      readonly: !this.editable,
      tabActiva: "identidad",
      clasificacionesTipo: [],
      /** Otros items del mismo emplazamiento (pestaña Ubicación). */
      mismoLugar: [],
      mismoLugarCargando: false,
      /** Tabla que se está viendo en el popup (pestaña Características técnicas). */
      tablaCaract: null,
      tablaCaractCargando: false,
      tablaCaractCrudo: '',
      /** Copia editable de la tabla del popup; solo se manda al servidor lo que cambie. */
      editandoTabla: false,
      guardandoTabla: false,
      tablaEdit: [],
      /** Clasificación completa del item (consulta a items_clasificacion) para la pestaña Clasificación. */
      clasificacionItem: null,
      idClasificacionCargada: null,
      tiposItem: [
        { valor: "AE",    label: "Aparatos Elevadores" },
        { valor: "REBT",  label: "Baja Tensión" },
        { valor: "REAT",  label: "Alta Tensión" },
        { valor: "AP",    label: "Aparatos a Presión" },
        { valor: "RITE",  label: "Instalaciones Térmicas" },
        { valor: "PI",    label: "Protección Contra Incendios" },
        { valor: "SM",    label: "Seguridad en Máquinas" },
        { valor: "SMMIN", label: "Maquinaria Minera Móvil" },
        { valor: "IP",    label: "Instalaciones Petrolíferas" },
        { valor: "EST",   label: "Estanterías" },
        { valor: "APQ",   label: "Almacenamiento Químicos" },
        { valor: "GC",    label: "Gases Combustibles" }
      ],
      defaultData: {
        id: "",
        tipo: "",
        naturaleza: "",
        materia: "",
        estado: "ACTIVO",
        subtipos: [],
        caracteristicas: {},
        warningsSync: []
      },
      eventosItem: [],
      /** Dossier documental del item (items_documentos). */
      documentosItem: [],
      /** Roles del item (items_roles): vigentes e históricos. */
      rolesItem: [],
      /** Alta/cambio manual de rol. */
      mostrarFormRol: false,
      nuevoRol: { rol: "", razonSocial: "", nif: "", habilitacion: "" },
      /** Campos editables de la propuesta al incorporar (valor + ámbito + añadir/quitar). */
      propuestaCampos: [],
      /** Características técnicas que la IA propone actualizar con el documento (con check). */
      caractActualizadas: [],
      celdasActualizadas: [],
      /** Edición de campos en el detalle del evento. */
      editandoEvento: false,
      edicionCampos: [],
      /** Edición manual de características técnicas (clave -> valor). */
      editandoCaract: false,
      caractEdit: {},
      /** Característica cuyo histórico se muestra en el popup. */
      caractDetalle: null,
      /** Catálogo de tipos de evento, para el select de corrección de la propuesta. */
      tiposEventoCatalogo: [],
      /** Estado del flujo "Incorporar documentación (IA)". */
      docDragOver: false,
      docAnalizando: false,
      docNarracion: null,
      docIdEnCurso: null,
      /** ItemDocumentoDTO con propuesta pendiente de confirmar/descartar. */
      docPropuesta: null,
      docTipoCorregido: "",
      /** Descripción editable del tipo (cuando es nuevo de la IA o se crea a mano). */
      docTipoDescripcion: "",
      /** Nombre del tipo nuevo cuando se elige "Crear un tipo nuevo" en el desplegable. */
      docNuevoNombre: "",
      docError: null,
      eventosFiltro: "",
      eventosSortField: "fecha",
      eventosSortDir: "desc",
      inspectoresPopoverList: [],
      /** Evento cuyo detalle (totalizado de campos) se muestra en el popup. */
      eventoDetalle: null,
      serviciosFiltro: "",
      serviciosSortField: "servicio",
      serviciosSortDir: "asc"
    };
  },
  props: {
    id: {
      type: String
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    hasMapContent(obj) {
      return obj && typeof obj === "object" && !Array.isArray(obj) && Object.keys(obj).length > 0;
    },
    pct(v) {
      if (v == null || isNaN(v)) return "";
      return Math.round(v * 100) + "%";
    },
    formatFecha(f) {
      if (!f) return "";
      try {
        var d = new Date(f);
        return d.toLocaleDateString("es-ES");
      } catch (e) { return "" + f; }
    },
    labelTipo(tipo) {
      var t = this.tiposItem.find(function (x) { return x.valor === tipo; });
      return t ? t.label : tipo || '';
    },
    /** Descripción de la estrategia de identidad según la naturaleza (estilo mockup). */
    etiquetaEstrategia(naturaleza) {
      switch (naturaleza) {
        case 'FIJ': return 'FIJ — Fijo (identificado por localización)';
        case 'MOV': return 'MOV — Móvil (identificado por marca/modelo/serie)';
        default: return naturaleza || '—';
      }
    },
    /** Copia el valor completo (sin recortar) al portapapeles, con toast de confirmación. */
    copiar(valor) {
      var self = this;
      if (!valor) return;
      var confirmar = function () {
        self.$f7.toast.create({
          text: 'Copiado al portapapeles',
          position: 'center',
          closeTimeout: 1500,
          icon: '<i class="f7-icons">doc_on_doc</i>'
        }).open();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(valor).then(confirmar).catch(function () {
          self.copiarFallback(valor);
          confirmar();
        });
      } else {
        // Contextos sin Clipboard API (http local, webview antigua): textarea + execCommand.
        self.copiarFallback(valor);
        confirmar();
      }
    },
    copiarFallback(valor) {
      var ta = document.createElement('textarea');
      ta.value = valor;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* sin soporte: no rompemos */ }
      document.body.removeChild(ta);
    },
    /**
     * Ajusta la fuente del ID canónico de la pestaña Identidad para que quepa entero en
     * su hueco: parte de 15px y encoge proporcionalmente hasta un mínimo de 1em (14px);
     * si ni así entra, se queda en el mínimo y la elipsis del CSS corta el resto (el
     * valor completo está en el tooltip).
     */
    ajustarTamanoId() {
      var el = this.$el && this.$el.querySelector('.kv-id');
      if (!el || !el.clientWidth) return; // no montado o pestaña oculta (v-show)
      var BASE = 15;
      var MIN = 14; // 1em de la ficha
      if (el.style.fontSize !== BASE + 'px') el.style.fontSize = BASE + 'px';
      if (el.scrollWidth > el.clientWidth) {
        var escala = el.clientWidth / el.scrollWidth;
        var nuevo = Math.max(MIN, Math.floor(BASE * escala));
        if (el.style.fontSize !== nuevo + 'px') el.style.fontSize = nuevo + 'px';
      }
    },
    /** Línea descriptiva bajo el id: tipo + titular + emplazamiento. */
    subtituloItem(formData) {
      var partes = [];
      if (formData.tipo) partes.push(this.labelTipo(formData.tipo));
      if (formData.titularActual && formData.titularActual.razonSocial) partes.push(formData.titularActual.razonSocial);
      var dir = this.formatDireccion(formData.emplazamiento);
      if (dir) partes.push(dir);
      return partes.join(' · ');
    },
    /** Ruta tipo / subtipos para el bloque "Clase de item" de la cabecera. */
    claseItem(formData) {
      var partes = [formData.tipo].concat(formData.subtipos || []).filter(Boolean);
      return partes.length ? partes.join(' / ') : '—';
    },
    /**
     * % de tiempo transcurrido entre la última inspección y la próxima (para el meter del
     * KPI). null si faltan fechas o el intervalo no es positivo.
     */
    meterProximaPct(formData) {
      if (!formData.fechaUltimaInspeccion || !formData.proximaInspeccion) return null;
      var ini = new Date(formData.fechaUltimaInspeccion).getTime();
      var fin = new Date(formData.proximaInspeccion).getTime();
      if (!(fin > ini)) return null;
      var pctv = Math.round(((Date.now() - ini) / (fin - ini)) * 100);
      return Math.max(0, Math.min(100, pctv));
    },
    urlVerExpediente(id) {
      // URL absoluta con hash-bang para abrir en pestaña nueva sin que el router
      // de F7 intercepte la navegación (mismo patrón que urlVerTipo en clasificaciones).
      return window.location.origin + '/#!/inspecciones/view/' + encodeURIComponent(id) + '/';
    },
    /**
     * Vuelve la próxima inspección a modo automático: al guardar, el backend la recalcula
     * desde el evento de inspección más reciente (proximaInspeccionManual=false en el PUT).
     */
    volverProximaAutomatica() {
      const self = this;
      const rform = self.$refs.rform;
      if (!rform || !rform.formData) return;
      self.$set(rform.formData, 'proximaInspeccionManual', false);
      self.$f7.toast.create({
        text: 'Próxima inspección en modo automático: se recalculará al guardar.',
        closeTimeout: 3500, closeButton: true
      }).open();
    },
    urlVerClasificacion(id) {
      return window.location.origin + '/#!/itemclasificacion/view/' + encodeURIComponent(id) + '/';
    },
    urlVerServicio(id) {
      return window.location.origin + '/#!/servicios/view/' + encodeURIComponent(id) + '/';
    },
    /** Dirección en una línea: "direccion, poblacion (provincia)". */
    formatDireccion(d) {
      if (!d) return '';
      var partes = [d.direccion, d.poblacion].filter(function (p) { return !!p; });
      var txt = partes.join(', ');
      if (d.provincia) txt += (txt ? ' ' : '') + '(' + d.provincia + ')';
      return txt;
    },
    /** "codigo - nombre" (o solo el código si no hay nombre). */
    formatMateria(m) {
      if (!m || !m.codigo) return '';
      return m.nombre ? (m.codigo + ' - ' + m.nombre) : m.codigo;
    },
    /** "username - nombre" (o solo el username si no hay nombre). */
    formatInspector(i) {
      if (!i) return '';
      return i.nombre ? ((i.username || '') + ' - ' + i.nombre) : (i.username || '');
    },
    /** Valor de una entrada de ev.mapaDatos por clave (p.ej. 'servicioNombre', 'materia'). */
    campoEvento(ev, clave) {
      return ev && ev.mapaDatos && ev.mapaDatos[clave] != null ? ev.mapaDatos[clave] : null;
    },
    /**
     * Código del expediente origen, resuelto contra los eventos INSPECCION ya cargados
     * (que llevan idExpediente + codigoExpediente en mapaDatos). Si el evento del origen
     * ya no está (p. ej. versionado), se muestra el id tal cual.
     */
    codigoExpedienteOrigen(formData) {
      var self = this;
      var idOrigen = formData.expedienteOrigen;
      if (!idOrigen) return '';
      var ev = (this.eventosItem || []).find(function (e) {
        return self.campoEvento(e, 'idExpediente') === idOrigen;
      });
      return (ev && self.campoEvento(ev, 'codigoExpediente')) || idOrigen;
    },
    /** Eventos del item cuyos tipo.codigo estén en la lista dada, en el orden del servidor (fecha desc). */
    eventosDeTipos(codigos) {
      return (this.eventosItem || []).filter(function (ev) {
        return ev.tipo && codigos.indexOf(ev.tipo.codigo) !== -1;
      });
    },
    eventosInspeccionList() {
      return this.eventosDeTipos(['INSPECCION']);
    },
    /** Código del tipo de evento de la pestaña dinámica activa (clave 'ev:CODIGO'), o null. */
    codigoEventoActivo() {
      return (this.tabActiva && this.tabActiva.indexOf('ev:') === 0) ? this.tabActiva.slice(3) : null;
    },
    /** Nombre legible del tipo de evento de la pestaña dinámica activa. */
    nombreTipoEventoActivo() {
      var cod = this.codigoEventoActivo();
      if (!cod) return '';
      var ev = (this.eventosItem || []).find(function (e) { return e.tipo && e.tipo.codigo === cod; });
      return (ev && ev.tipo && ev.tipo.nombre) || cod;
    },
    /** Orden de presentación de un tipo, según el catálogo (items_eventos_tipos); 9999 si no está. */
    ordenTipo(codigo) {
      var cat = this.tiposEventoCatalogo || [];
      for (var i = 0; i < cat.length; i++) {
        if (cat[i].codigo === codigo) return (cat[i].orden != null ? cat[i].orden : 9999);
      }
      return 9999;
    },
    /**
     * Pestañas visibles: las fijas siempre; las de eventos solo si el item tiene eventos
     * de ese tipo (dinámicas). Los separadores replican los grupos del mockup.
     */
    pestanas(formData) {
      var self = this;
      var tabs = [
        { key: 'identidad', label: 'Identidad' },
        { key: 'clasificacion', label: 'Clasificación' }
      ];
      // Una pestaña por cada tipo de evento presente (menos CAMBIO_TITULARIDAD, que va en Roles),
      // ordenadas por el 'orden' del catálogo (items_eventos_tipos), editable desde BBDD.
      // INSPECCION usa su pestaña rica; el resto, el bloque genérico ('ev:CODIGO').
      var tiposPresentes = {};
      (self.eventosItem || []).forEach(function (ev) {
        if (ev.tipo && ev.tipo.codigo && ev.tipo.codigo !== 'CAMBIO_TITULARIDAD') {
          if (!tiposPresentes[ev.tipo.codigo]) {
            tiposPresentes[ev.tipo.codigo] = { codigo: ev.tipo.codigo, nombre: ev.tipo.nombre || ev.tipo.codigo, count: 0 };
          }
          tiposPresentes[ev.tipo.codigo].count++;
        }
      });
      Object.keys(tiposPresentes).map(function (c) { return tiposPresentes[c]; })
        .sort(function (a, b) {
          var oa = self.ordenTipo(a.codigo), ob = self.ordenTipo(b.codigo);
          return oa !== ob ? oa - ob : (a.nombre || '').localeCompare(b.nombre || '');
        })
        .forEach(function (t) {
          if (t.codigo === 'INSPECCION') tabs.push({ key: 'inspecciones', label: 'Inspecciones', count: t.count });
          else tabs.push({ key: 'ev:' + t.codigo, label: t.nombre, count: t.count });
        });
      tabs.push({ key: 'tecnicas', label: 'Características técnicas', count: this.numCaracteristicas(formData) || null });
      tabs.push({ key: 'roles', label: 'Titularidad y roles' });
      tabs.push({ key: 'incorporar', label: 'Incorporar documentación', ia: true });
      tabs.push({ key: 'documentacion', label: 'Documentación', count: (this.documentosItem || []).length || null });
      tabs.push({ key: 'trazabilidad', label: 'Trazabilidad', count: (this.eventosItem || []).length || null });
      tabs.push({ key: 'auditoria', label: 'Auditoría' });
      // Al final: es contexto del entorno del item, no del item en sí.
      tabs.push({ key: 'ubicacion', label: 'Ubicación' });
      return tabs;
    },
    /** Todos los eventos en orden cronológico ascendente para la línea temporal. */
    /** Hitos de la traza: eventos (items_eventos) + altas/bajas de rol (items_roles), en orden. */
    timelineHitos() {
      var self = this;
      var hitos = [];
      (self.eventosItem || []).forEach(function (ev) {
        // Los cambios de titularidad no entran como evento: ya se ven como alta del rol Titular.
        if (ev.tipo && ev.tipo.codigo === 'CAMBIO_TITULARIDAD') return;
        hitos.push({
          fecha: ev.fecha,
          color: self.colorTl(ev),
          titulo: self.tituloTl(ev),
          desc: self.descTl(ev),
          kind: 'evento',
          ev: ev
        });
      });
      (self.rolesItem || []).forEach(function (r) {
        if (r.fechaDesde) {
          var prev = self.rolAnterior(r);
          hitos.push({
            fecha: r.fechaDesde,
            color: 'role',
            titulo: r.rolNombre + ': ' + (r.razonSocial || '—'),
            desc: self.origenRolTexto(r) + (prev ? ' · antes: ' + (prev.razonSocial || '—') : ''),
            kind: 'rol',
            rol: r
          });
        }
        // Baja sin relevo (CERRADO): las HISTORICO ya se reflejan con el alta del sucesor.
        if (r.fechaHasta && r.estado === 'CERRADO') {
          hitos.push({
            fecha: r.fechaHasta,
            color: 'bad',
            titulo: 'Baja de ' + r.rolNombre + ': ' + (r.razonSocial || '—'),
            desc: '',
            kind: 'rol',
            rol: r
          });
        }
      });
      hitos.sort(function (a, b) {
        var ta = a.fecha ? new Date(a.fecha).getTime() : 0;
        var tb = b.fecha ? new Date(b.fecha).getTime() : 0;
        return ta - tb;
      });
      return hitos;
    },
    /**
     * Rol al que sustituye éste: el del mismo tipo que se cerró justo cuando éste empezó
     * (fechaHasta == fechaDesde). Es el vínculo real de vigencia, no la cronología (las fechas
     * pueden estar mezcladas: documentos con fecha antigua, sembrado del titular reciente...).
     * null si no sustituye a nadie (primera asignación de ese rol).
     */
    rolAnterior(r) {
      if (!r || !r.rol || !r.fechaDesde) return null;
      var td = new Date(r.fechaDesde).getTime();
      var found = null;
      (this.rolesItem || []).forEach(function (x) {
        if (x === r || x.rol !== r.rol || !x.fechaHasta) return;
        if (new Date(x.fechaHasta).getTime() === td) found = x;
      });
      return found;
    },
    /** Click en un hito: evento -> abre su detalle; rol -> salta a la pestaña Roles. */
    clickHito(h) {
      if (!h) return;
      if (h.kind === 'evento' && h.ev) this.abrirDetalleEvento(h.ev);
      else if (h.kind === 'rol') this.tabActiva = 'roles';
    },
    /** Color del punto de la línea temporal por tipo de evento (paleta del mockup). */
    colorTl(ev) {
      var codigo = ev.tipo && ev.tipo.codigo;
      switch (codigo) {
        case 'AUTORIZACION': return 'teal';
        case 'MODIFICACION':
        case 'AMPLIACION':
        case 'REPARACION': return 'mut';
        case 'MANTENIMIENTO': return 'ok';
        case 'CAMBIO_TITULARIDAD': return 'ok';
        case 'INSPECCION': return 'warn';
        default: return '';
      }
    },
    tituloTl(ev) {
      var titulo = (ev.tipo && ev.tipo.nombre) || '';
      var codigoExp = this.campoEvento(ev, 'codigoExpediente');
      if (codigoExp) titulo += ' · ' + codigoExp;
      return titulo;
    },
    descTl(ev) {
      var self = this;
      if (ev.tipo && ev.tipo.codigo === 'CAMBIO_TITULARIDAD') {
        var ant = self.campoEvento(ev, 'titularAnterior');
        var nue = self.campoEvento(ev, 'titularNuevo');
        if (ant || nue) return (ant || '¿?') + ' → ' + (nue || '¿?');
      }
      var desc = self.campoEvento(ev, 'descripcion');
      if (desc) return desc;
      var partes = [
        self.campoEvento(ev, 'razonSocialCliente'),
        self.campoEvento(ev, 'servicioNombre'),
        self.formatMateria(self.campoEvento(ev, 'materia'))
      ].filter(Boolean);
      return partes.join(' · ');
    },
    /** Valor por el que se ordena una inspección según la columna elegida (null = va al final). */
    valorOrdenEvento(ev, campo) {
      var self = this;
      switch (campo) {
        case 'fecha': return ev.fecha ? new Date(ev.fecha).getTime() : null;
        case 'expediente': return self.campoEvento(ev, 'codigoExpediente') || self.campoEvento(ev, 'idExpediente') || null;
        case 'cliente': return self.campoEvento(ev, 'razonSocialCliente') || self.campoEvento(ev, 'codigoCliente') || null;
        case 'proximaInspeccion': {
          var p = self.campoEvento(ev, 'proximaInspeccion');
          return p ? new Date(p).getTime() : null;
        }
        case 'servicio': return self.campoEvento(ev, 'servicioNombre') || null;
        case 'materia': {
          var m = self.campoEvento(ev, 'materia');
          return (m && (m.codigo || m.nombre)) || null;
        }
        case 'inspectores': {
          var insp = self.campoEvento(ev, 'inspectores');
          return (insp && insp.length) ? self.formatInspector(insp[0]) : null;
        }
        default: return null;
      }
    },
    /** Click en una cabecera: si ya se ordenaba por esa columna, invierte; si no, la fija (fecha por defecto en descendente, el resto en ascendente). */
    ordenarEventosPor(campo) {
      if (this.eventosSortField === campo) {
        this.eventosSortDir = this.eventosSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.eventosSortField = campo;
        this.eventosSortDir = campo === 'fecha' ? 'desc' : 'asc';
      }
    },
    indicadorOrden(campo) {
      if (this.eventosSortField !== campo) return '';
      return this.eventosSortDir === 'asc' ? ' ▲' : ' ▼';
    },
    /** Inspecciones filtradas (texto libre sobre los campos visibles) y ordenadas por la columna elegida. */
    eventosOrdenadosFiltrados(eventos) {
      var self = this;
      var lista = (eventos || []).slice();
      var filtro = (this.eventosFiltro || '').trim().toLowerCase();
      if (filtro) {
        lista = lista.filter(function (ev) {
          var materia = self.campoEvento(ev, 'materia');
          var inspectores = self.campoEvento(ev, 'inspectores');
          var texto = [
            ev.tipo && ev.tipo.nombre, self.campoEvento(ev, 'codigoExpediente'), self.campoEvento(ev, 'idExpediente'),
            self.campoEvento(ev, 'razonSocialCliente'), self.campoEvento(ev, 'codigoCliente'),
            self.campoEvento(ev, 'servicioNombre'), materia && materia.codigo, materia && materia.nombre,
            self.campoEvento(ev, 'descripcion'),
            (inspectores || []).map(function (i) { return i.nombre || i.username; }).join(' ')
          ].filter(Boolean).join(' ').toLowerCase();
          return texto.indexOf(filtro) !== -1;
        });
      }
      var campo = this.eventosSortField;
      var dir = this.eventosSortDir === 'asc' ? 1 : -1;
      lista.sort(function (a, b) {
        var va = self.valorOrdenEvento(a, campo);
        var vb = self.valorOrdenEvento(b, campo);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
      return lista;
    },
    /**
     * Servicios distintos (por servicioId) entre los eventos, con las materias que traen
     * asociadas (normalmente una, pero se listan todas por si el mismo servicio apareció
     * con materias distintas en eventos diferentes).
     */
    serviciosDeEventos(eventos) {
      var self = this;
      var porId = {};
      (eventos || []).forEach(function (ev) {
        var servicioId = self.campoEvento(ev, 'servicioId');
        if (!servicioId) return;
        if (!porId[servicioId]) {
          porId[servicioId] = { servicioId: servicioId, servicioNombre: self.campoEvento(ev, 'servicioNombre'), materias: {} };
        }
        var materia = self.campoEvento(ev, 'materia');
        if (materia && materia.codigo) {
          porId[servicioId].materias[materia.codigo] = materia.nombre;
        }
      });
      return Object.keys(porId).map(function (id) {
        var s = porId[id];
        var materiasTexto = Object.keys(s.materias).sort().map(function (cod) {
          return s.materias[cod] ? (cod + ' - ' + s.materias[cod]) : cod;
        }).join(', ');
        return { servicioId: s.servicioId, servicioNombre: s.servicioNombre, materiasTexto: materiasTexto };
      });
    },
    ordenarServiciosPor(campo) {
      if (this.serviciosSortField === campo) {
        this.serviciosSortDir = this.serviciosSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.serviciosSortField = campo;
        this.serviciosSortDir = 'asc';
      }
    },
    indicadorOrdenServicios(campo) {
      if (this.serviciosSortField !== campo) return '';
      return this.serviciosSortDir === 'asc' ? ' ▲' : ' ▼';
    },
    serviciosOrdenadosFiltrados(eventos) {
      var lista = this.serviciosDeEventos(eventos);
      var filtro = (this.serviciosFiltro || '').trim().toLowerCase();
      if (filtro) {
        lista = lista.filter(function (s) {
          var texto = [s.servicioNombre, s.materiasTexto].filter(Boolean).join(' ').toLowerCase();
          return texto.indexOf(filtro) !== -1;
        });
      }
      var campo = this.serviciosSortField === 'materias' ? 'materiasTexto' : 'servicioNombre';
      var dir = this.serviciosSortDir === 'asc' ? 1 : -1;
      lista.sort(function (a, b) {
        var va = (a[campo] || '').toLowerCase();
        var vb = (b[campo] || '').toLowerCase();
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
      return lista;
    },
    /** Eventos del item (colección items_eventos), cargados aparte del payload del item. */
    cargarEventos() {
      var self = this;
      if (!self.id) return;
      ItemEnriquecido.eventosDeItem(self.id).then(function (eventos) {
        self.eventosItem = eventos || [];
      }).catch(function () {
        self.eventosItem = [];
      });
    },
    /** Los tres tipos de tabla no se vuelcan en la ficha: se ven en un popup. */
    esTablaCaract(c) {
      return !!c && ['TABLA', 'TABLA_DINAMICA', 'TABLA_PERSONALIZADA'].indexOf(c.tipoCaracteristica) >= 0;
    },
    /** Una celda es editable salvo que el formato la marque de solo lectura (personalizadas). */
    celdaEditable(fila, columna) {
      var ed = (this.tablaCaract || {}).editables || [];
      if (!ed.length) return true;
      var f = ed[fila];
      if (!f || f[columna] === undefined || f[columna] === null) return true;
      return f[columna] !== false;
    },
    iniciarEdicionTabla() {
      var filas = (this.tablaCaract || {}).filas || [];
      this.tablaEdit = filas.map(function (f) { return (f || []).map(function (c) { return c == null ? '' : String(c); }); });
      this.editandoTabla = true;
    },
    guardarTabla() {
      var self = this;
      if (self.guardandoTabla || !self.tablaCaract) return;
      var original = self.tablaCaract.filas || [];
      var celdas = [];
      self.tablaEdit.forEach(function (f, fi) {
        f.forEach(function (valor, ci) {
          var antes = (original[fi] || [])[ci];
          antes = antes == null ? '' : String(antes);
          if (valor !== antes && self.celdaEditable(fi, ci)) {
            celdas.push({ clave: self.tablaCaract.clave, fila: fi, columna: ci, valor: valor });
          }
        });
      });
      if (!celdas.length) { self.editandoTabla = false; return; }
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que lo soltaba.
      cerrojoDeLaSesion(self, 'guardandoTabla');
      ItemEnriquecido.editarCeldasTabla(self.id, celdas).then(function () {
        // Se recarga desde el servidor: en las personalizadas lo que se guarda es el HTML y el
        // resultado real lo dice él, no esta copia.
        return ItemEnriquecido.tablaCaracteristica(self.id, self.tablaCaract.clave);
      }).then(function (t) {
        self.guardandoTabla = false;
        self.editandoTabla = false;
        if (t) self.tablaCaract = t;
        self.$f7.toast.show({ text: celdas.length + ' celda' + (celdas.length > 1 ? 's' : '') + ' guardada' + (celdas.length > 1 ? 's' : ''), closeTimeout: 2500 });
        self.recargarItem();
      }).catch(function (err) {
        self.guardandoTabla = false;
        console.warn('[tabla] no se pudo guardar', err);
        self.$f7.dialog.alert((err && (err.message || err.error)) || 'No se pudieron guardar los cambios.', 'Edición de tabla');
      });
    },
    abrirTablaCaract(c) {
      const self = this;
      if (!self.id || !c) return;
      self.editandoTabla = false;
      self.tablaEdit = [];
      // Se abre ya, con lo que se sabe, y el contenido llega después: el parseo lo hace el
      // servidor (los tres tipos se guardan distinto) y no conviene duplicarlo aquí.
      self.tablaCaractCrudo = c.valorActual != null ? String(c.valorActual) : '';
      self.tablaCaract = { clave: c.nombre, label: c.label || c.nombre, tipo: c.tipoCaracteristica, filas: [], interpretada: true };
      cerrojoDeLaSesion(self, 'tablaCaractCargando');
      ItemEnriquecido.tablaCaracteristica(self.id, c.nombre).then(function (t) {
        self.tablaCaractCargando = false;
        if (t) self.tablaCaract = t;
      }).catch(function (err) {
        self.tablaCaractCargando = false;
        self.tablaCaract = Object.assign({}, self.tablaCaract, { interpretada: false });
        console.warn('[tabla] no se pudo cargar', err);
      });
    },
    cargarMismoLugar() {
      var self = this;
      if (!self.id) return;
      cerrojoDeLaSesion(self, 'mismoLugarCargando');
      ItemEnriquecido.mismoLugar(self.id).then(function (items) {
        self.mismoLugar = items || [];
        self.mismoLugarCargando = false;
      }).catch(function () {
        self.mismoLugar = [];
        self.mismoLugarCargando = false;
      });
    },
    /**
     * URL del mapa embebido del emplazamiento. Prioriza las coordenadas (exactas) y, si no las
     * hay, busca por la dirección normalizada. Con {@code output=embed} no hace falta API key,
     * así que la clave de Google no viaja al navegador.
     */
    urlMapa(e) {
      if (!e) return null;
      var base = 'https://maps.google.com/maps?output=embed&z=17&q=';
      if (e.latitud != null && e.longitud != null && e.latitud !== '' && e.longitud !== '') {
        return base + encodeURIComponent(e.latitud + ',' + e.longitud);
      }
      var texto = e.direccionNormalizada
        || [e.direccion, e.cp, e.poblacion, e.provincia].filter(Boolean).join(', ');
      return texto ? base + encodeURIComponent(texto) : null;
    },
    /** URL absoluta de la ficha de un item (ruta hash-bang), para abrirla fuera de la SPA. */
    urlItem(id) {
      return window.location.origin + '/#!/itemsenriquecidos/view/' + encodeURIComponent(id) + '/';
    },
    /**
     * Navega a la ficha de otro item del mismo emplazamiento. Con Ctrl/Cmd (o clic central) se
     * abre en otra pestaña; se hace con window.open y no con target="_blank" porque el router de
     * Framework7 intercepta los href internos y se quedaría en la misma pestaña.
     */
    irAItem(id, ev) {
      if (!id) return;
      if (ev && (ev.ctrlKey || ev.metaKey || ev.button === 1)) {
        window.open(this.urlItem(id), '_blank', 'noopener');
        return;
      }
      this.$f7router.navigate('/itemsenriquecidos/view/' + encodeURIComponent(id) + '/');
    },
    cargarRoles() {
      var self = this;
      if (!self.id) return;
      ItemEnriquecido.rolesDeItem(self.id).then(function (roles) {
        self.rolesItem = roles || [];
      }).catch(function () {
        self.rolesItem = [];
      });
    },
    /** Roles vigentes (sin fecha de baja), ordenados por importancia del rol. */
    rolesVigentes() {
      var orden = { TITULAR: 1, CONTRATANTE: 2, EMPRESA_INSTALADORA: 3, EMPRESA_MANTENEDORA: 4, ORGANISMO_CONTROL: 5 };
      return (this.rolesItem || []).filter(function (r) { return r.vigente; })
        .slice().sort(function (a, b) { return (orden[a.rol] || 9) - (orden[b.rol] || 9); });
    },
    /** Roles ya cerrados (histórico de cambios), más reciente primero. */
    rolesHistoricos() {
      return (this.rolesItem || []).filter(function (r) { return !r.vigente; })
        .slice().sort(function (a, b) { return new Date(b.fechaHasta || 0) - new Date(a.fechaHasta || 0); });
    },
    /** Texto de origen de un rol; si vino de un documento, muestra el nombre del fichero. */
    origenRolTexto(r) {
      if (!r) return '—';
      if (r.origen === 'DOCUMENTO') {
        var doc = (this.documentosItem || []).find(function (d) { return d.idEvento === r.referencia; });
        return doc ? 'Documento: ' + this.nombreDocumento(doc.documento) : 'Documento';
      }
      if (r.origen === 'SYNC') return 'Sync';
      if (r.origen === 'MANUAL') return 'Manual';
      return r.origen || '—';
    },
    /** Alta o cambio manual de un rol (cierra el vigente del mismo tipo y abre el nuevo). */
    guardarRolManual() {
      var self = this;
      if (!self.id) return;
      if (!self.nuevoRol.rol) { self.$f7.dialog.alert('Elige el rol.'); return; }
      if (!self.nuevoRol.razonSocial || !self.nuevoRol.razonSocial.trim()) { self.$f7.dialog.alert('Escribe la entidad.'); return; }
      ItemRol.cambiar({
        idItem: self.id,
        rol: self.nuevoRol.rol,
        razonSocial: self.nuevoRol.razonSocial.trim(),
        nif: self.nuevoRol.nif,
        habilitacion: self.nuevoRol.habilitacion
      }).then(function (roles) {
        self.rolesItem = roles || [];
        self.mostrarFormRol = false;
        self.nuevoRol = { rol: "", razonSocial: "", nif: "", habilitacion: "" };
        self.$f7.toast.create({ text: 'Rol actualizado.', closeTimeout: 2000 }).open();
      }).catch(function (error) {
        self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'Error al guardar el rol.');
      });
    },
    /** Da de baja (cierra) un rol vigente. */
    cerrarRol(r) {
      var self = this;
      if (!r || !r.id) return;
      self.$f7.dialog.confirm('¿Dar de baja el rol "' + r.rolNombre + ' — ' + (r.razonSocial || '') + '"?', 'Cerrar rol', function () {
        ItemRol.cerrar(r.id).then(function (roles) {
          self.rolesItem = roles || [];
          self.$f7.toast.create({ text: 'Rol dado de baja.', closeTimeout: 2000 }).open();
        }).catch(function (error) {
          self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'Error al cerrar el rol.');
        });
      });
    },
    // -----------------------------------------------------------------
    // Incorporar documentación (IA) + Documentación
    // -----------------------------------------------------------------
    /**
     * Dossier del item. Si hay una propuesta pendiente o un análisis a medias (recarga de
     * página), se retoman: la propuesta se re-muestra y el análisis se re-engancha al SSE.
     */
    cargarDocumentos() {
      var self = this;
      if (!self.id) return;
      ItemDocumento.deItem(self.id).then(function (docs) {
        self.documentosItem = docs || [];
        if (!self.docPropuesta) {
          var propuesto = self.documentosItem.find(function (d) { return d.estado === 'PROPUESTO'; });
          if (propuesto) {
            self.docPropuesta = propuesto;
            self.resetEdicionTipo();
          }
        }
        var analizando = self.documentosItem.find(function (d) { return d.estado === 'ANALIZANDO'; });
        if (analizando && !self.docAnalizando) {
          self.docIdEnCurso = analizando.id;
          self.docAnalizando = true;
          self.suscribirDocEvents();
        }
      }).catch(function () {
        self.documentosItem = [];
      });
    },
    cargarTiposEvento() {
      var self = this;
      ItemDocumento.tiposEvento().then(function (tipos) {
        self.tiposEventoCatalogo = tipos || [];
      }).catch(function () {
        self.tiposEventoCatalogo = [];
      });
    },
    /** Etiqueta legible de un campo (del catálogo de tipos); si no está, el propio nombre. */
    etiquetaCampo(clave) {
      var cat = this.tiposEventoCatalogo || [];
      for (var i = 0; i < cat.length; i++) {
        var campos = cat[i].campos || [];
        for (var j = 0; j < campos.length; j++) {
          if (campos[j].nombre === clave && campos[j].etiqueta) return campos[j].etiqueta;
        }
      }
      return clave;
    },
    /** Columnas de la tabla de un grupo de eventos: unión de sus camposListado, en orden. */
    columnasListado(eventos) {
      var cols = [];
      (eventos || []).forEach(function (ev) {
        (ev.camposListado || []).forEach(function (c) { if (cols.indexOf(c) < 0) cols.push(c); });
      });
      return cols;
    },
    /** Valor de un campo del evento formateado para mostrar (fechas, materia, inspectores, objetos). */
    valorEventoTexto(ev, clave) {
      var v = this.campoEvento(ev, clave);
      if (v === null || v === undefined || v === '') return '';
      if (clave === 'inspectores' && Array.isArray(v)) return v.map(this.formatInspector).join(', ');
      if (v && typeof v === 'object') {
        if (v.codigo && v.nombre) return this.formatMateria(v);
        if (v.direccion || v.poblacion || v.cp) return [v.direccion, v.cp, v.poblacion].filter(Boolean).join(' ');
        try { return JSON.stringify(v); } catch (e) { return String(v); }
      }
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return this.formatFecha(v);
      return String(v);
    },
    /** Totalizado: TODOS los campos del evento (listado + detalle + cualquier clave de mapaDatos). */
    camposTotalizado(ev) {
      if (!ev) return [];
      var claves = [];
      (ev.camposListado || []).forEach(function (c) { if (claves.indexOf(c) < 0) claves.push(c); });
      (ev.camposDetalle || []).forEach(function (c) { if (claves.indexOf(c) < 0) claves.push(c); });
      // Además, cualquier otra clave presente en mapaDatos que no esté declarada (no ocultar nada).
      if (ev.mapaDatos) {
        Object.keys(ev.mapaDatos).forEach(function (c) { if (claves.indexOf(c) < 0) claves.push(c); });
      }
      var self = this;
      return claves.map(function (c) {
        return { clave: c, etiqueta: self.etiquetaCampo(c), valor: self.valorEventoTexto(ev, c) };
      });
    },
    abrirDetalleEvento(ev) {
      this.editandoEvento = false;
      this.eventoDetalle = ev;
    },
    /** Documentos del dossier asociados a un evento (doc.idEvento === evento.id), para verlos desde su detalle. */
    documentosDeEvento(ev) {
      if (!ev || !ev.id) return [];
      return (this.documentosItem || []).filter(function (d) { return d.idEvento === ev.id; });
    },
    /** Un evento se puede borrar si NO viene del sync (los del sync llevan idExpediente en mapaDatos). */
    eventoBorrable(ev) {
      return !!ev && !(ev.mapaDatos && ev.mapaDatos.idExpediente);
    },
    /** Marca de edición de un campo (quién y cuándo), o null si no se editó a mano. */
    edicionCampo(ev, clave) {
      return (ev && ev.edicionesCampos && ev.edicionesCampos[clave]) || null;
    },
    /** Entra en modo edición de los campos del evento (construye la tabla editable). */
    iniciarEdicionEvento() {
      var self = this;
      var ev = self.eventoDetalle;
      if (!ev) return;
      var listado = ev.camposListado || [];
      var mapa = ev.mapaDatos || {};
      var lista = [];
      Object.keys(mapa).forEach(function (k) {
        if (k === 'idDocumento' || k === 'idExpediente') return;
        var raw = mapa[k];
        lista.push({
          nombre: k,
          etiqueta: self.etiquetaCampo(k),
          valor: (raw != null && typeof raw === 'object') ? self.valorEventoTexto(ev, k) : (raw != null ? String(raw) : ''),
          ambito: listado.indexOf(k) >= 0 ? 'LISTADO' : 'DETALLE',
          manual: false
        });
      });
      self.edicionCampos = lista;
      self.editandoEvento = true;
    },
    anadirCampoEdicion() {
      this.edicionCampos.push({ nombre: '', etiqueta: '', valor: '', ambito: 'DETALLE', manual: true });
    },
    guardarEdicionEvento() {
      var self = this;
      var ev = self.eventoDetalle;
      if (!ev || !ev.id) return;
      // Volver a los campos de un evento es corregir lo que extrajo la IA: el motivo es obligatorio
      // y se pide antes de guardar, igual que en la propuesta.
      self.pedirMotivo('Guardar los campos',
        'Qué ha corregido y por qué (lo usamos para que la IA mejore)', function (motivo) {
          ItemDocumento.editarCamposEvento(ev.id, self.camposAPayload(self.edicionCampos), motivo)
            .then(function (actualizado) {
              self.eventoDetalle = actualizado || null;
              self.editandoEvento = false;
              self.$f7.toast.create({ text: 'Campos actualizados.', closeTimeout: 2000 }).open();
              self.cargarEventos();
              self.cargarTiposEvento();
            }).catch(function (error) {
              self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'No se pudo guardar.');
            });
        });
    },
    /** Borra el evento (y su documento asociado) desde el popup de detalle. */
    eliminarEventoDetalle(ev) {
      var self = this;
      if (!ev || !ev.id) return;
      var tieneDoc = self.documentosDeEvento(ev).length > 0;
      var msg = 'Se eliminará este evento' + (tieneDoc ? ' y su documento asociado' : '') + '. Esta acción no se puede deshacer. ¿Continuar?';
      self.$f7.dialog.confirm(msg, 'Eliminar evento', function () {
        ItemDocumento.eliminarEvento(ev.id).then(function () {
          self.eventoDetalle = null;
          self.$f7.toast.create({ text: 'Evento eliminado.', closeTimeout: 2000 }).open();
          self.cargarEventos();
          self.cargarDocumentos();
          self.cargarRoles();
          self.recargarItem();
        }).catch(function (error) {
          self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'No se pudo eliminar el evento.');
        });
      });
    },
    /** Borra un documento del dossier; si tiene evento asociado, pregunta si borrarlo también. */
    eliminarDocumento(d) {
      var self = this;
      if (!d || !d.id) return;
      if (d.idEvento) {
        self.$f7.dialog.create({
          title: 'Eliminar documento',
          text: 'Este documento tiene un evento asociado. ¿Qué quieres borrar?',
          verticalButtons: true,
          buttons: [
            { text: 'Solo el documento', onClick: function () { self._eliminarDocumento(d, false); } },
            { text: 'Documento y evento', color: 'red', onClick: function () { self._eliminarDocumento(d, true); } },
            { text: 'Cancelar', color: 'gray' }
          ]
        }).open();
      } else {
        self.$f7.dialog.confirm('Se eliminará este documento. ¿Continuar?', 'Eliminar documento', function () {
          self._eliminarDocumento(d, false);
        });
      }
    },
    _eliminarDocumento(d, borrarEvento) {
      var self = this;
      ItemDocumento.eliminar(d.id, borrarEvento).then(function () {
        self.$f7.toast.create({ text: borrarEvento ? 'Documento y evento eliminados.' : 'Documento eliminado.', closeTimeout: 2000 }).open();
        self.cargarDocumentos();
        self.cargarEventos();
        self.cargarRoles();
        self.recargarItem();
      }).catch(function (error) {
        self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'No se pudo eliminar el documento.');
      });
    },
    nombreDocumento(doc) {
      if (!doc) return '';
      return (doc.nombre || 'documento') + (doc.extension ? '.' + doc.extension : '');
    },
    urlDescargaDocumento(d) {
      if (!d || !d.documento) return '#';
      return Documento.getDocumentoPublicUrl(d.documento);
    },
    labelOrigenDoc(origen) {
      switch (origen) {
        case 'SUBIDO': return 'Subida manual';
        case 'EXPEDIENTE': return 'Expediente';
        case 'PEDIDO': return 'Pedido';
        case 'OFERTA': return 'Oferta';
        default: return origen || '—';
      }
    },
    claseEstadoDoc(estado) {
      switch (estado) {
        case 'CONFIRMADO': return 'st-cur';
        case 'PROPUESTO': return 'st-soon';
        case 'ANALIZANDO': return 'st-soon';
        case 'ERROR': return 'st-bad';
        default: return 'st-clo';
      }
    },
    tipoEventoDeDocumento(d) {
      if (d && d.propuesta && (d.propuesta.tipoNombre || d.propuesta.tipoCodigo)) {
        return d.propuesta.tipoNombre || d.propuesta.tipoCodigo;
      }
      if (d && d.origen === 'EXPEDIENTE') return 'Inspección';
      return '—';
    },
    esCampoNuevo(clave) {
      var p = this.docPropuesta && this.docPropuesta.propuesta;
      if (!p || !p.camposNuevos) return false;
      return p.camposNuevos.some(function (c) { return c && c.nombre === clave; });
    },
    /** Etiqueta legible de un campo de la propuesta: la de la IA (camposNuevos), o la del catálogo, o el propio código. */
    etiquetaCampoPropuesta(clave) {
      var p = this.docPropuesta && this.docPropuesta.propuesta;
      if (p && p.camposNuevos) {
        var nuevo = p.camposNuevos.find(function (c) { return c && c.nombre === clave; });
        if (nuevo && nuevo.etiqueta) return nuevo.etiqueta;
      }
      return this.etiquetaCampo(clave);
    },
    /** Al mostrar una propuesta: precarga la descripción del tipo y limpia el modo "crear nuevo". */
    resetEdicionTipo() {
      var p = this.docPropuesta && this.docPropuesta.propuesta;
      this.docTipoCorregido = '';
      this.docNuevoNombre = '';
      this.docTipoDescripcion = (p && p.tipoDescripcion) || '';
      this.construirPropuestaCampos();
      this.construirCaractActualizadas();
      this.construirCeldasActualizadas();
    },
    /** Monta la lista de características que la IA propone actualizar (con su valor actual del item). */
    construirCaractActualizadas() {
      var self = this;
      var p = self.docPropuesta && self.docPropuesta.propuesta;
      var lista = (p && p.caracteristicasActualizadas) || [];
      var rf = self.$refs.rform;
      var carMap = (rf && rf.formData && rf.formData.caracteristicas) || {};
      self.caractActualizadas = lista.map(function (c) {
        var actual = carMap[c.nombre] || {};
        return {
          nombre: c.nombre,
          label: actual.label || c.nombre,
          valorAnterior: (actual.valorActual != null && actual.valorActual !== '') ? String(actual.valorActual) : '—',
          valorNuevo: c.valorNuevo != null ? String(c.valorNuevo) : '',
          aplicar: true
        };
      });
    },
    /**
     * Monta la lista de celdas de tabla que la IA propone rellenar. El valor actual de cada celda
     * se pide al servidor —es él quien sabe leer los tres formatos de tabla— y llega después, así
     * que la fila se pinta ya y el "valor actual" se rellena cuando responde.
     */
    construirCeldasActualizadas() {
      var self = this;
      var p = self.docPropuesta && self.docPropuesta.propuesta;
      var lista = (p && p.celdasActualizadas) || [];
      var rf = self.$refs.rform;
      var carMap = (rf && rf.formData && rf.formData.caracteristicas) || {};
      self.celdasActualizadas = lista.map(function (c) {
        var actual = carMap[c.nombre] || {};
        return {
          nombre: c.nombre,
          label: actual.label || c.nombre,
          fila: c.fila,
          columna: c.columna,
          valorAnterior: '…',
          valorNuevo: c.valorNuevo != null ? String(c.valorNuevo) : '',
          aplicar: true
        };
      });
      // Una petición por tabla distinta, no una por celda.
      var claves = self.celdasActualizadas.map(function (c) { return c.nombre; })
        .filter(function (n, i, arr) { return arr.indexOf(n) === i; });
      claves.forEach(function (clave) {
        if (!self.id) return;
        ItemEnriquecido.tablaCaracteristica(self.id, clave).then(function (t) {
          var filas = (t && t.filas) || [];
          self.celdasActualizadas.forEach(function (c) {
            if (c.nombre !== clave) return;
            var v = (filas[c.fila] || [])[c.columna];
            c.valorAnterior = (v != null && v !== '') ? String(v) : '(vacía)';
          });
        }).catch(function () { /* se queda con el marcador; el cambio sigue siendo aplicable */ });
      });
    },
    /** Ámbito propuesto por la IA para un campo (LISTADO/DETALLE); DETALLE si no consta. */
    ambitoCampoPropuesta(clave) {
      var p = this.docPropuesta && this.docPropuesta.propuesta;
      if (p && p.camposNuevos) {
        var c = p.camposNuevos.find(function (x) { return x && x.nombre === clave; });
        if (c && c.ambito === 'LISTADO') return 'LISTADO';
      }
      return 'DETALLE';
    },
    /** Monta la tabla editable de la propuesta desde los campos de la IA. */
    construirPropuestaCampos() {
      var self = this;
      var p = self.docPropuesta && self.docPropuesta.propuesta;
      var campos = (p && p.campos) || {};
      var lista = [];
      Object.keys(campos).forEach(function (k) {
        lista.push({
          nombre: k,
          etiqueta: self.etiquetaCampoPropuesta(k),
          valor: campos[k] != null ? String(campos[k]) : '',
          ambito: self.ambitoCampoPropuesta(k),
          esNuevo: self.esCampoNuevo(k),
          manual: false
        });
      });
      self.propuestaCampos = lista;
    },
    /** Añade una fila de campo nuevo a mano en la propuesta. */
    anadirCampoPropuesta() {
      this.propuestaCampos.push({ nombre: '', etiqueta: '', valor: '', ambito: 'DETALLE', esNuevo: true, manual: true });
    },
    /** Convierte una etiqueta en un nombre técnico lowerCamelCase. */
    slugCampo(txt) {
      if (!txt) return '';
      var sinAcentos = txt.normalize ? txt.normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '') : txt;
      var parts = sinAcentos.trim().toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      if (!parts.length) return '';
      return parts[0] + parts.slice(1).map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join('');
    },
    /** Convierte una lista de campos editables (propuesta o detalle) al payload del backend. */
    camposAPayload(lista) {
      var self = this;
      var out = [];
      (lista || []).forEach(function (c) {
        var nombre = (c.nombre && c.nombre.trim()) || self.slugCampo(c.etiqueta);
        if (!nombre) return;
        out.push({
          nombre: nombre,
          etiqueta: (c.etiqueta && c.etiqueta.trim()) || nombre,
          valor: c.valor != null ? String(c.valor) : '',
          ambito: c.ambito || 'DETALLE',
          editado: !!c.manual
        });
      });
      return out;
    },
    /** Al cambiar el desplegable de tipo: ajusta la descripción editable según el modo elegido. */
    onCambioTipoPropuesta() {
      var p = this.docPropuesta && this.docPropuesta.propuesta;
      if (this.docTipoCorregido === '__NUEVO__') {
        this.docNuevoNombre = '';
        this.docTipoDescripcion = '';
      } else if (this.docTipoCorregido === '') {
        this.docTipoDescripcion = (p && p.tipoDescripcion) || '';
      }
    },
    onFicheroDocChange(e) {
      var file = e.target.files && e.target.files[0];
      e.target.value = '';
      this.subirDocumento(file);
    },
    /** Soltar un fichero sobre la caja de "Incorporar documentación". */
    onDropDoc(e) {
      this.docDragOver = false;
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file && !/\.(pdf|jpe?g|png|webp)$/i.test(file.name || '')) {
        this.$f7.toast.create({ text: 'Formato no admitido: sube un PDF o una imagen (jpg, png, webp).', closeTimeout: 3500 }).open();
        return;
      }
      this.subirDocumento(file);
    },
    subirDocumento(file, forzar) {
      var self = this;
      if (!file || !self.id) return;
      self.docError = null;
      self.docNarracion = { latin: 'Uploading', texto: 'Subiendo y archivando el documento…' };
      self.docAnalizando = true;
      console.log('[ItemDoc] Subiendo documento', { nombre: file.name, tamanoKB: Math.round((file.size || 0) / 1024), idItem: self.id, forzar: !!forzar });
      ItemDocumento.procesar(self.id, file, forzar).then(function (res) {
        if (res && res.duplicado) {
          self.docAnalizando = false;
          self.docNarracion = null;
          var fecha = res.fechaExistente ? self.formatFecha(res.fechaExistente) : null;
          console.warn('[ItemDoc] Documento ya incorporado (hash duplicado). idDocumentoExistente =', res.idDocumentoExistente);
          self.$f7.dialog.confirm(
            'Este documento ya está incorporado' + (fecha ? ' (evento del ' + fecha + ')' : '') +
            '. ¿Volver a analizarlo de todas formas? Se creará como un evento aparte.',
            'Documento ya incorporado',
            function () { self.subirDocumento(file, true); }
          );
          return;
        }
        if (res && res.lanzado === false && res.enCurso) {
          self.docAnalizando = false;
          console.warn('[ItemDoc] No se lanzó: ya hay un análisis en curso.');
          self.$f7.toast.create({ text: 'Ya hay un análisis en curso; espera a que termine.', closeTimeout: 3500 }).open();
          return;
        }
        self.docIdEnCurso = res && res.idDocumento;
        console.log('[ItemDoc] Análisis lanzado en backend. idDocumento =', self.docIdEnCurso);
        self.suscribirDocEvents();
        // Red de seguridad: si el SSE no entrega el 'fin' (proxy/carrera), el polling
        // detecta el estado final del documento y recupera la UI igualmente.
        self.iniciarPollDoc();
      }).catch(function (error) {
        self.docAnalizando = false;
        self.docError = (error && (error.message || error.error)) || error || 'Error subiendo el documento.';
        console.error('[ItemDoc] Error subiendo el documento:', error);
      });
    },
    suscribirDocEvents() {
      if (!this._docHandler) this._docHandler = this._onDocEvent.bind(this);
      console.log('[ItemDoc] Suscribiendo al SSE de narración (item-documento).');
      ItemDocumentoEvents.subscribe(this._docHandler, this.$f7);
    },
    _onDocEvent(payload) {
      var self = this;
      var esMio = !(self.docIdEnCurso && payload && payload.idDocumento !== self.docIdEnCurso);
      console.log('[ItemDoc] SSE recibido:', payload && payload.estado,
        '| idDocumento:', payload && payload.idDocumento,
        esMio ? '(mío)' : '(otro documento, ignorado)',
        payload && payload.texto ? '· ' + payload.texto : '');
      if (!payload || (self.docIdEnCurso && payload.idDocumento !== self.docIdEnCurso)) return;
      if (payload.estado === 'progreso') {
        self.docAnalizando = true;
        self.docNarracion = { latin: 'Reading', texto: payload.texto };
      } else if (payload.estado === 'fin') {
        self.finalizarAnalisis(payload.documento || null);
      } else if (payload.estado === 'error') {
        self.docError = payload.texto || 'Error analizando el documento.';
        self.finalizarAnalisis(null);
      }
    },
    /**
     * Cierra el análisis en curso. Idempotente: lo puede disparar el SSE 'fin'/'error' o el
     * polling de respaldo, el que llegue primero. Para el polling y desuscribe del SSE.
     */
    finalizarAnalisis(documento) {
      var self = this;
      if (!self.docAnalizando && !self.docIdEnCurso) return; // ya cerrado por la otra vía
      self.docAnalizando = false;
      self.docNarracion = null;
      self.docIdEnCurso = null;
      if (documento) { self.docPropuesta = documento; self.resetEdicionTipo(); }
      self.pararPollDoc();
      if (self._docHandler) ItemDocumentoEvents.unsubscribe(self._docHandler);
      self.cargarDocumentos();
    },
    /**
     * Fallback por sondeo del estado del documento cada 3s (máx ~2min). Cubre el caso de que
     * el SSE no entregue el 'fin' a tiempo (buffering del proxy en local, o carrera al
     * suscribirse tras el POST): el backend ya dejó el doc en PROPUESTO/ERROR y aquí se detecta.
     */
    iniciarPollDoc() {
      var self = this;
      self.pararPollDoc();
      var intentos = 0;
      self._docPoll = setInterval(function () {
        intentos++;
        if (!self.docIdEnCurso) { self.pararPollDoc(); return; }
        if (intentos > 40) {
          console.warn('[ItemDoc] Polling agotado (~2 min) sin estado final para', self.docIdEnCurso);
          self.pararPollDoc();
          return;
        }
        ItemDocumento.deItem(self.id).then(function (docs) {
          var d = (docs || []).find(function (x) { return x.id === self.docIdEnCurso; });
          if (!d) return;
          if (d.estado === 'PROPUESTO') {
            console.log('[ItemDoc] Polling detectó PROPUESTO (intento ' + intentos + '); recuperando UI. El SSE no entregó el fin a tiempo.');
            self.finalizarAnalisis(d);
          } else if (d.estado === 'ERROR') {
            console.warn('[ItemDoc] Polling detectó ERROR en el documento.');
            self.docError = (d.propuesta && d.propuesta.error) || 'Error analizando el documento.';
            self.finalizarAnalisis(null);
          }
        }).catch(function () {});
      }, 3000);
    },
    pararPollDoc() {
      if (this._docPoll) { clearInterval(this._docPoll); this._docPoll = null; }
    },
    /**
     * Pide el motivo de una corrección y llama a `alAceptar(motivo)` solo si tiene contenido real.
     * El aviso se da aquí, ANTES de guardar, que es la condición que puso Dirección: la API lo
     * vuelve a exigir, pero el usuario no debe enterarse por un error del servidor.
     */
    pedirMotivo(titulo, texto, alAceptar) {
      const self = this;
      self.$f7.dialog.prompt(texto, titulo, function (motivo) {
        if (!motivo || motivo.trim().length < MOTIVO_MINIMO) {
          self.$f7.dialog.alert('Explica el motivo con al menos ' + MOTIVO_MINIMO
            + ' caracteres: es lo que permite que la IA aprenda del error.', titulo);
          return;
        }
        alAceptar(motivo.trim());
      });
    },
    /**
     * «Lo acepto tal cual»: un clic, sin motivo. No se manda nada de la pantalla —el servidor
     * aplica la propuesta que tiene guardada—, así que tampoco se mira si el usuario tocó algo:
     * mirarlo sería volver a la lista de cosas que hay que comparar, que es justo lo que se ha
     * quitado.
     */
    aceptarPropuestaTalCual() {
      const self = this;
      if (!self.docPropuesta) return;
      ItemDocumento.aceptarTalCual(self.docPropuesta.id)
        .then(function () { self.trasConfirmarPropuesta(true); })
        .catch(function (error) {
          self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'Error al confirmar.');
        });
    },
    /** «Guardar mis correcciones»: lo que hay en la pantalla, con el motivo por delante. */
    guardarCorreccionPropuesta() {
      const self = this;
      if (!self.docPropuesta) return;
      self.pedirMotivo('Guardar correcciones',
        'Qué ha corregido de la propuesta de la IA y por qué (lo usamos para que mejore)',
        function (motivo) { self.confirmarPropuesta(motivo); });
    },
    confirmarPropuesta(motivo) {
      var self = this;
      if (!self.docPropuesta) return;
      var p = self.docPropuesta.propuesta || {};
      // `aceptarTalCual: false` viaja SIEMPRE, también cuando no se ha tocado nada: es lo que
      // distingue esta pantalla de una pestaña abierta antes del despliegue, a la que el servidor
      // responde que recargue en vez de dar por aceptada una propuesta que nadie ha mirado.
      const payload = { idDocumento: self.docPropuesta.id, aceptarTalCual: false, motivo: motivo };
      if (self.docTipoCorregido === '__NUEVO__') {
        // Tipo nuevo creado a mano: el backend normaliza el código a UPPER_SNAKE desde el nombre.
        var nombreNuevo = (self.docNuevoNombre || '').trim();
        if (!nombreNuevo) { self.$f7.dialog.alert('Escribe el nombre del tipo nuevo.'); return; }
        payload.tipoCodigo = nombreNuevo;
        payload.tipoNombre = nombreNuevo;
        payload.tipoDescripcion = (self.docTipoDescripcion || '').trim();
      } else if (self.docTipoCorregido) {
        // Tipo existente elegido en el desplegable.
        var tipo = self.tiposEventoCatalogo.find(function (t) { return t.codigo === self.docTipoCorregido; });
        payload.tipoCodigo = self.docTipoCorregido;
        payload.tipoNombre = tipo ? tipo.nombre : self.docTipoCorregido;
      } else if (p.tipoEsNuevo) {
        // Propuesta de la IA con tipo nuevo: se puede editar su descripción antes de crearlo.
        payload.tipoDescripcion = (self.docTipoDescripcion || '').trim();
      }
      // Campos finales (valor + ámbito, incluidos los añadidos/quitados a mano).
      payload.campos = self.camposAPayload(self.propuestaCampos);
      // Características técnicas marcadas para actualizar.
      payload.caracteristicasItem = (self.caractActualizadas || [])
        .filter(function (c) { return c.aplicar; })
        .map(function (c) { return { clave: c.nombre, valor: c.valorNuevo }; });
      // Celdas de tablas: van por separado porque de una tabla solo se escribe la celda indicada.
      payload.celdasItem = (self.celdasActualizadas || [])
        .filter(function (c) { return c.aplicar; })
        .map(function (c) { return { clave: c.nombre, fila: c.fila, columna: c.columna, valor: c.valorNuevo }; });
      ItemDocumento.confirmar(payload).then(function () {
        self.trasConfirmarPropuesta(false);
      }).catch(function (error) {
        self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'Error al confirmar.');
      });
    },
    /**
     * Limpieza y recarga tras confirmar, por los dos caminos. `talCual` dice cuál fue: al aceptar
     * tal cual se aplican TODAS las características propuestas, estén marcadas o no en pantalla,
     * porque es la propuesta guardada la que se aplica.
     */
    trasConfirmarPropuesta(talCual) {
      const self = this;
      // Reflejar en la vista las características actualizadas (mutación reactiva en sitio).
      const rf = self.$refs.rform;
      const map = (rf && rf.formData && rf.formData.caracteristicas) || {};
      (self.caractActualizadas || []).forEach(function (c) {
        if ((talCual || c.aplicar) && map[c.nombre]) map[c.nombre].valorActual = c.valorNuevo;
      });
      self.caractActualizadas = [];
      self.celdasActualizadas = [];
      self.docPropuesta = null;
      self.docTipoCorregido = '';
      self.docTipoDescripcion = '';
      self.docNuevoNombre = '';
      self.$f7.toast.create({
        text: 'Evento creado y documento archivado.',
        position: 'center', closeTimeout: 2500,
        icon: '<i class="f7-icons">checkmark_alt</i>'
      }).open();
      // Refresca eventos, dossier, catálogo (por si nació un tipo/campo nuevo) y el item
      // (materias/última X recalculadas en servidor).
      self.cargarEventos();
      self.cargarDocumentos();
      self.cargarTiposEvento();
      self.cargarRoles();
      self.recargarItem();
    },
    descartarPropuesta() {
      var self = this;
      if (!self.docPropuesta) return;
      // El motivo sustituye al "¿seguro?": descartar es decir que la IA se equivocó, y escribir por
      // qué es a la vez la confirmación y lo que más enseña del error.
      self.pedirMotivo('Descartar la propuesta',
        'Se borrará el documento y su propuesta. ¿Por qué no sirve?',
        function (motivo) {
          ItemDocumento.descartar(self.docPropuesta.id, motivo).then(function () {
            self.docPropuesta = null;
            self.docTipoCorregido = '';
            self.docTipoDescripcion = '';
            self.docNuevoNombre = '';
            self.caractActualizadas = [];
            self.celdasActualizadas = [];
            self.cargarDocumentos();
          }).catch(function (error) {
            self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'Error al descartar.');
          });
        });
    },
    recargarItem() {
      var self = this;
      if (!self.id || !self.$refs.rform) return;
      ItemEnriquecido.get({ itemId: self.id }).then(function (res) {
        var dto = res && (res.dto || res);
        if (dto && dto.id) self.$refs.rform.formData = dto;
      }).catch(function () { /* la ficha sigue con los datos que tenía */ });
    },
    /**
     * Id de la clasificación a la que navegar: el guardado por el sync en
     * clasificacionAplicada o, si no vino (p. ej. tipificación parcial sin
     * clasificación exacta en el prefijo), se localiza por tipo + subtipos entre
     * las clasificaciones cargadas para el selector de completar tipología.
     */
    idClasificacionNavegable(formData) {
      var ca = formData.clasificacionAplicada;
      if (!ca) return null;
      if (ca.idClasificacion) return ca.idClasificacion;
      var tipo = ca.tipo || formData.tipo;
      var subtipos = formData.subtipos || [];
      var match = this.clasificacionesTipo.find(function (c) {
        if (c.codigo !== tipo) return false;
        var s = c.subtipos || [];
        if (s.length !== subtipos.length) return false;
        for (var i = 0; i < s.length; i++) {
          if (s[i] !== subtipos[i]) return false;
        }
        return true;
      });
      return match ? match.id : null;
    },
    cargarClasificacionesTipo() {
      var self = this;
      ItemClasificacion.getList({ page: 1, limit: 1000 }, true)
        .then(function (res) {
          var items = (res && (res.list || res.content || res.data || res)) || [];
          if (!Array.isArray(items) && items.content) items = items.content;
          self.clasificacionesTipo = (items || []).filter(function (it) { return it && it.codigo; });
          // Reintento de la consulta de clasificación: si el item cargó antes que esta lista
          // y su clasificacionAplicada no traía id, el match por tipo+subtipos ya es posible.
          if (self.$refs.rform && self.$refs.rform.formData) {
            self.cargarClasificacionItem(self.$refs.rform.formData);
          }
        })
        .catch(function () { self.clasificacionesTipo = []; });
    },
    /** Al cargar el item en el rForm: consulta de su clasificación para la pestaña Clasificación. */
    onLoadItem(item) {
      this.cargarClasificacionItem(item || (this.$refs.rform && this.$refs.rform.formData) || {});
      this.cargarMismoLugar();
    },
    /** Carga (una vez por id) la clasificación completa del item desde items_clasificacion. */
    cargarClasificacionItem(formData) {
      var self = this;
      var idClasif = self.idClasificacionNavegable(formData || {});
      if (!idClasif || idClasif === self.idClasificacionCargada) return;
      self.idClasificacionCargada = idClasif;
      ItemClasificacion.get({ itemId: idClasif }).then(function (res) {
        self.clasificacionItem = (res && (res.dto || res)) || null;
      }).catch(function () {
        self.clasificacionItem = null;
      });
    },
    /**
     * Opciones para completar una tipología parcial: clasificaciones del mismo tipo
     * cuyos subtipos extienden el prefijo actual del item.
     */
    opcionesCompletarTipologia(formData) {
      var tipo = (formData.clasificacionAplicada && formData.clasificacionAplicada.tipo) || formData.tipo || '';
      var prefijo = formData.subtipos || [];
      return this.clasificacionesTipo
        .filter(function (c) {
          if (c.codigo !== tipo) return false;
          var s = c.subtipos || [];
          if (s.length <= prefijo.length) return false;
          for (var i = 0; i < prefijo.length; i++) {
            if (s[i] !== prefijo[i]) return false;
          }
          return true;
        })
        .map(function (c) {
          return {
            key: (c.subtipos || []).join('|'),
            label: c.codigo + ' / ' + (c.subtipos || []).join(' / '),
            subtipos: c.subtipos || []
          };
        });
    },
    onCompletarTipologia(rform, key) {
      if (!key) return;
      var opt = this.opcionesCompletarTipologia(rform.formData).find(function (o) { return o.key === key; });
      if (!opt) return;
      this.$set(rform.formData, 'subtipos', opt.subtipos.slice());
      this.$set(rform.formData, 'subtipo', opt.subtipos.length ? opt.subtipos[0] : null);
      this.$set(rform.formData, 'tipificacionParcial', false);
    },
    /**
     * Agrupa el mapa de características del item por grupoCodigo, usando los grupos de
     * clasificacionAplicada para el orden y la denominación. Las que no tienen grupo
     * (items antiguos sin canonicalizar) van a un grupo final "Otras".
     */
    numCaracteristicas(formData) {
      var carMap = formData.caracteristicas || {};
      return Object.keys(carMap).length;
    },
    gruposConCaracteristicas(formData) {
      var carMap = formData.caracteristicas || {};
      var gruposClasif = (formData.clasificacionAplicada && formData.clasificacionAplicada.grupos) || [];
      var porGrupo = {};
      Object.keys(carMap).forEach(function (nombre) {
        var c = carMap[nombre] || {};
        var gc = c.grupoCodigo || "__OTRAS__";
        if (!porGrupo[gc]) porGrupo[gc] = [];
        porGrupo[gc].push(Object.assign({ nombre: nombre }, c));
      });
      var resultado = [];
      gruposClasif.forEach(function (g) {
        if (!porGrupo[g.codigo]) return;
        porGrupo[g.codigo].sort(function (a, b) { return (a.label || a.nombre).localeCompare(b.label || b.nombre); });
        resultado.push({ codigo: g.codigo, denominacion: g.denominacion || g.codigo, items: porGrupo[g.codigo] });
        delete porGrupo[g.codigo];
      });
      // Grupos con características pero sin entrada en clasificacionAplicada (o items antiguos)
      Object.keys(porGrupo).forEach(function (gc) {
        porGrupo[gc].sort(function (a, b) { return (a.label || a.nombre).localeCompare(b.label || b.nombre); });
        resultado.push({ codigo: gc, denominacion: gc === "__OTRAS__" ? "Otras (sin grupo)" : gc, items: porGrupo[gc] });
      });
      return resultado;
    },
    /** Abre el popup con el histórico de valores de una característica. */
    abrirHistoricoCaract(c) {
      this.caractDetalle = c;
    },
    /** Histórico de una característica, más reciente primero. */
    historicoOrdenado(c) {
      return ((c && c.historico) || []).slice().reverse();
    },
    /** Entra en modo edición de características: copia los valores actuales a un buffer editable. */
    iniciarEdicionCaract() {
      var rf = this.$refs.rform;
      var carMap = (rf && rf.formData && rf.formData.caracteristicas) || {};
      var edit = {};
      Object.keys(carMap).forEach(function (k) {
        var v = carMap[k] && carMap[k].valorActual;
        edit[k] = (v == null) ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
      });
      this.caractEdit = edit;
      this.editandoCaract = true;
    },
    /** Guarda solo las características cuyo valor cambió; archiva el anterior en el histórico (backend). */
    guardarCaract() {
      var self = this;
      var rf = self.$refs.rform;
      var carMap = (rf && rf.formData && rf.formData.caracteristicas) || {};
      var cambios = [];
      Object.keys(self.caractEdit).forEach(function (k) {
        var actual = carMap[k] && carMap[k].valorActual;
        var actualStr = (actual == null) ? '' : (typeof actual === 'object' ? JSON.stringify(actual) : String(actual));
        if ((self.caractEdit[k] || '') !== actualStr) {
          cambios.push({ clave: k, valor: self.caractEdit[k] });
        }
      });
      if (!cambios.length) { self.editandoCaract = false; return; }
      ItemEnriquecido.editarCaracteristicas(self.id, cambios).then(function () {
        // Muta el valorActual existente (propiedad reactiva) para que la vista se refresque
        // sin depender de reasignar todo formData (el slot de r-form no reacciona bien a eso).
        var map = (self.$refs.rform && self.$refs.rform.formData && self.$refs.rform.formData.caracteristicas) || {};
        cambios.forEach(function (ch) {
          if (map[ch.clave]) map[ch.clave].valorActual = ch.valor;
        });
        self.editandoCaract = false;
        self.$f7.toast.create({ text: 'Características actualizadas.', closeTimeout: 2000 }).open();
      }).catch(function (error) {
        self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'No se pudieron guardar las características.');
      });
    },
    editar() {
      this.readonly = false;
      this.tabActiva = 'identidad';
    },
    /** Reclasifica el item (re-sincroniza sus expedientes) y recarga la ficha. */
    reclasificar() {
      var self = this;
      if (!self.id) return;
      self.$f7.dialog.confirm(
        'Se reclasificará el item re-sincronizando sus expedientes: re-deduce la tipología/subtipos y consolida las características contra el maestro actual. ¿Continuar?',
        'Reclasificar item',
        function () {
          var pre = self.$f7.dialog.preloader('Reclasificando…');
          ItemEnriquecido.reclasificar(self.id).then(function () {
            pre.close();
            self.$f7.toast.create({ text: 'Item reclasificado.', closeTimeout: 2500 }).open();
            try { self.$f7.views.current.router.refreshPage(); } catch (e) { /* si falla, recargar a mano */ }
          }).catch(function (error) {
            pre.close();
            self.$f7.dialog.alert((error && (error.message || error.error)) || error || 'No se pudo reclasificar.');
          });
        }
      );
    },
    beforeSave(data, resolve, reject) {
      var errors = {};
      if (!data.id || !data.id.trim()) {
        errors.id = "El ID canónico es obligatorio";
      }
      if (!data.tipo) {
        errors.tipo = "El tipo es obligatorio";
      }
      if (Object.keys(errors).length) {
        reject({ message: "Revise los campos obligatorios", errors });
        return;
      }
      if (!data.posibleIdentificadorErroneo) {
        data.motivoPosibleIdentificadorErroneo = null;
      }
      resolve(data);
    },
    deleteItem() {
      var self = this;
      var app = self.$f7;
      app.dialog.confirm(
        self.$t("rlist.pregunta_eliminar_registro"),
        self.$t("common.eliminar"),
        function() {
          ItemEnriquecido.delete({ itemId: self.id }).then(function(res) {
            if (res) {
              app.toast.create({
                icon: '<i class="f7-icons">checkmark</i>',
                text: self.$t("rlist.registro_eliminado"),
                position: "center",
                destroyOnClose: true,
                closeTimeout: 2000
              }).open();
              self.$f7router.back();
            }
          }).catch(function(error) {
            app.dialog.alert(error.message || error.error || error);
          });
        }
      );
    },
    onSave(res) {
      var self = this;
      var app = self.$f7;
      self.$refs.rform.formData = res;
      self.readonly = true;
      app.toast.create({
        text: self.$t("common.guardado_ok") || "Guardado correctamente",
        position: "center",
        closeTimeout: 2000,
        icon: '<i class="f7-icons">checkmark_alt</i>'
      }).open();
    },
  },
  mounted() {
    this.cargarClasificacionesTipo();
    this.cargarEventos();
    this.cargarDocumentos();
    this.cargarTiposEvento();
    this.cargarRoles();
    this.ajustarTamanoId();
  },
  // Tras cada re-render (carga del item, cambio de pestaña, volver de edición...) se
  // re-evalúa el encaje del ID. Solo toca el DOM (style), no dispara otro render.
  updated() {
    var self = this;
    this.$nextTick(function () { self.ajustarTamanoId(); });
  },
  beforeDestroy() {
    this.pararPollDoc();
    if (this._docHandler) ItemDocumentoEvents.unsubscribe(this._docHandler);
  }
};
</script>
